<template>
  <div class="ni-app-submission-form-wrapper eu-region">
    <b-loading :is-full-page="true" :active.sync="isLoading"> </b-loading>
    <p><strong>Important!</strong></p>
    <br />
    <p>
      Please remember the following while working on your New Item Submission:
    </p>
    <p>
      &#x2022; When submitting your new items, please ensure that validation is
      not in progress. Submitting while validation is in progress may result in
      the failure of the submission.
    </p>
    <p>
      &#x2022; Item attachments, such as Safety Data Sheets and CE Certificates,
      must be uploaded in PDF format. If your documents are in another format
      (for example .xlsx) please email to rsdeusuppliermgmt@thermofisher.com,
      with the New Item Submission ID in the subject line of the email.
    </p>
    <p>
      &#x2022; The limit for New Item Submissions is 20000 items. If you need to
      create more than 20000 items, please contact
      rsdeusuppliermgmt@thermofisher.com via email.
    </p>
    <p>Thank you,</p>
    <p>Fisher Scientific Data Content and Operations</p>
    <br />
    <h6>Submission - {{ euSubmissionID }}</h6>
    <h6 class="page-heading-wrapper eu-region">
      <a class="page-heading" v-show="showSubmissionDetail"
        >Submission Detail</a
      >
      <a class="page-heading" v-show="showItemDetail">Item Level Detail</a>
      <b-tag
        type="is-light"
        size="is-small"
        class="noedit-label"
        v-show="
          showSubmissionInReadOnly &&
            !this.isReworkItem &&
            !euEventType.reViewRequest
        "
        >The Submission cannot be edited in this view. To make edits as a
        supplier, please open it from "My Draft Submissions or My Rework
        Submissions" queues.</b-tag
      >
      <div
        class="tab-actions-wrapper"
        v-show="!euEventType.viewRequest && !euEventType.reViewRequest"
      >
        <b-button
          type="is-info"
          class="ni-app-add-item-button"
          title="Add Item"
          v-show="showItemDetail && activeTab === 0 && !this.isReworkItem"
          @click="triggerAdd = true"
          >Add</b-button
        >
        <b-button
          type="is-info"
          class="ni-app-delete-items-button"
          title="Delete"
          v-show="showItemDetail && activeTab === 0 && !this.isReworkItem"
          @click="deleteSubmissionItems"
          >Delete</b-button
        >
        <b-button
          type="is-info"
          class="ni-app-delete-items-button"
          title="Validate"
          v-show="showItemDetail"
          :disabled="disableSuppValidateBtn"
          @click="supplierValidate"
          >Validate</b-button
        >
      </div>
      <div class="actions-wrapper eu-region-actions-wrapper">
        <b-upload
          class="ni-app-delete-items-button custom-upload-item-level"
          v-model="submissionUploadFile"
          @input="uploadSubmissionData"
          v-show="
            showItemDetail &&
              !euEventType.viewRequest &&
              !euEventType.reViewRequest
          "
        >
          <a
            class="button is-info"
            title="This process can take up to 10 minutes when the number of records are 1000 or more."
          >
            <span>Upload Data</span>
          </a>
        </b-upload>
        <b-button type="is-info" @click="downloadSubmissionData()"
          >Download Data</b-button
        >
        <b-button
          type="is-info"
          @click="downloadSubmissionData('E')"
          v-show="
            (submissionStatus === 'Upload' ||
              submissionStatus === 'Processed') &&
              euEventType.viewRequest
          "
          >Download Errored Data</b-button
        >
        <b-button
          type="is-info"
          @click="downloadSubmissionData('P')"
          v-show="
            (submissionStatus === 'Upload' ||
              submissionStatus === 'Processed') &&
              euEventType.viewRequest
          "
          >Download Processed Data</b-button
        >
        <!--Submit button for suppliers-->
        <b-button
          type="is-info"
          @click="submitCancelWorkflow(euEventType.reViewRequest ? 'F' : 'S')"
          :disabled="disableSupplierSubmit"
          v-show="!euEventType.viewRequest"
          >Submit</b-button
        >
        <b-button
          type="is-info"
          @click="cancelSubmission"
          outlined
          v-show="
            !euEventType.viewRequest &&
              !euEventType.reViewRequest &&
              !this.isReworkItem
          "
          >Cancel</b-button
        >
      </div>
    </h6>
    <div class="filter-sort-eu-region-wrapper">
      <div class="eu-filter-sort-fields" v-show="showItemDetail">
        <div class="gi-tab-filter" v-show="activeTab === 0">
          <b-dropdown
            aria-role="menu"
            trap-focus
            class="eu-filter-only-wrapper"
          >
            <button class="button is-info is-outlined" slot="trigger">
              <span>Filter By</span>
            </button>
            <b-dropdown-item aria-role="menu-item" :focusable="false" custom>
              <b-field label="Errored Items">
                <b-select v-model="gifilterMapErrors">
                  <option value="YES">YES</option>
                  <option value="NO">NO</option>
                </b-select>
              </b-field>
              <b-field label="Supplier Item No."
                ><b-input
                  type="text"
                  :value="gifilterMapSuppItemNbr"
                  @change.native="gifilterMapSuppItemNbr = $event.target.value"
                ></b-input
              ></b-field>
              <b-field label="Product Category/Item Class">
                <b-select v-model="gifilterMapProdCtgry">
                  <option value="Equipment and Instruments"
                    >Equipment and Instruments</option
                  >
                  <option value="Biologicals">Biologicals</option>
                  <option value="Chemicals and Biochemical Reagents"
                    >Chemicals and Biochemical Reagents</option
                  >
                  <option value="Consumables">Consumables</option>
                  <option value="Diagnostics">Diagnostics</option>
                  <option value="Furniture">Furniture</option>
                  <option value="Training and Services"
                    >Training and Services</option
                  >
                  <option value="Safety Products">Safety Products</option>
                </b-select>
              </b-field>
              <b-field label="English Product Noun"
                ><b-input
                  type="text"
                  :value="gifilterMapProductNoun"
                  @change.native="gifilterMapProductNoun = $event.target.value"
                ></b-input
              ></b-field>
              <b-field label="Item Status - Rework – Additional Data Required"
                ><b-checkbox
                  size="is-large"
                  type="is-info"
                  v-model="gifilterMapReworkItems"
                ></b-checkbox
              ></b-field>
            </b-dropdown-item>
          </b-dropdown>
          <b-dropdown
            aria-role="menu"
            trap-focus
            class="eu-filter-sort-wrapper"
          >
            <button class="button is-info is-outlined" slot="trigger">
              <span>Sort By</span>
            </button>
            <b-dropdown-item aria-role="menu-item" :focusable="false" custom>
              <b-field label="Supplier Item No. Order">
                <b-select v-model="giSortMapSuppItemNbrOrder">
                  <option value="asc"> Ascending </option
                  ><option value="desc">Descending</option>
                </b-select>
              </b-field>
            </b-dropdown-item>
          </b-dropdown>
          <b-dropdown
            aria-role="menu"
            trap-focus
            class="eu-filter-search-wrapper"
          >
            <button class="button is-info is-outlined" slot="trigger">
              <span>Search By</span>
            </button>
            <b-dropdown-item aria-role="menu-item" :focusable="false" custom>
              <b-field label="Field Name">
                <b-select v-model="gisearchMapFieldName">
                  <option value="ErroredItems">Errored Items</option
                  ><option value="suppItemNbr">Supplier Item No.</option>
                </b-select>
              </b-field>
              <b-field
                label="Value"
                v-show="gisearchMapFieldName === 'ErroredItems'"
              >
                <b-select v-model="gisearchMapInput">
                  <option value="YES">YES</option>
                  <option value="NO">NO</option>
                </b-select>
              </b-field>
              <b-field
                label="Search"
                v-show="gisearchMapFieldName === 'suppItemNbr'"
              >
                <b-input
                  type="text"
                  v-model="gisearchMapInput"
                  @change.native="gisearchMapInput = $event.target.value"
                ></b-input>
              </b-field>
            </b-dropdown-item>
          </b-dropdown>
          <b-button
            type="is-info"
            outlined
            @click="
              loadActiveTabData();
              gifilterMapErrors ||
              gifilterMapSuppItemNbr ||
              gifilterMapProductNoun ||
              gifilterMapProdCtgry ||
              gifilterMapReworkItems
                ? (showCustomFilterAlert = true)
                : '';
            "
            class="item-level-apply-button"
          >
            Apply
          </b-button>
          <b-button
            type="is-info"
            outlined
            @click="
              resetFilterSortSearchInputs();
              showCustomFilterAlert = false;
            "
            class="item-level-reset-button"
          >
            Reset
          </b-button>
        </div>
        <div class="pi-tab-filter" v-show="activeTab === 1">
          <b-dropdown
            aria-role="menu"
            trap-focus
            class="eu-filter-only-wrapper"
          >
            <button class="button is-info is-outlined" slot="trigger">
              <span>Filter By</span>
            </button>
            <b-dropdown-item aria-role="menu-item" :focusable="false" custom>
              <b-field label="Errored Items">
                <b-select v-model="pifilterMapErrors">
                  <option value="YES">YES</option>
                  <option value="NO">NO</option>
                </b-select>
              </b-field>
              <b-field label="Supplier Item No."
                ><b-input
                  type="text"
                  :value="pifilterMapSuppItemNbr"
                  @change.native="pifilterMapSuppItemNbr = $event.target.value"
                ></b-input
              ></b-field>
              <b-field label="English Product Noun"
                ><b-input
                  type="text"
                  :value="pifilterMapProductNoun"
                  @change.native="pifilterMapProductNoun = $event.target.value"
                ></b-input
              ></b-field>
              <b-field label="Item Status - Rework – Additional Data Required"
                ><b-checkbox
                  size="is-large"
                  type="is-info"
                  v-model="pifilterMapReworkItems"
                ></b-checkbox
              ></b-field>
            </b-dropdown-item>
          </b-dropdown>
          <b-dropdown
            aria-role="menu"
            trap-focus
            class="eu-filter-sort-wrapper"
          >
            <button class="button is-info is-outlined" slot="trigger">
              <span>Sort By</span>
            </button>
            <b-dropdown-item aria-role="menu-item" :focusable="false" custom>
              <b-field label="Supplier Item No. Order">
                <b-select v-model="pisortMapSuppItemNbrOrder">
                  <option value="asc"> Ascending </option
                  ><option value="desc">Descending</option>
                </b-select>
              </b-field>
            </b-dropdown-item>
          </b-dropdown>
          <b-dropdown
            aria-role="menu"
            trap-focus
            class="eu-filter-search-wrapper"
          >
            <button class="button is-info is-outlined" slot="trigger">
              <span>Search By</span>
            </button>
            <b-dropdown-item aria-role="menu-item" :focusable="false" custom>
              <b-field label="Field Name">
                <b-select v-model="pisearchMapFieldName">
                  <option value="ErroredItems">Errored Items</option
                  ><option value="suppItemNbr">Supplier Item No.</option>
                </b-select>
              </b-field>
              <b-field
                label="Value"
                v-show="pisearchMapFieldName === 'ErroredItems'"
              >
                <b-select v-model="pisearchMapInput">
                  <option value="YES">YES</option>
                  <option value="NO">NO</option>
                </b-select>
              </b-field>
              <b-field
                label="Search"
                v-show="pisearchMapFieldName === 'suppItemNbr'"
              >
                <b-input
                  type="text"
                  v-model="pisearchMapInput"
                  @change.native="pisearchMapInput = $event.target.value"
                ></b-input>
              </b-field>
            </b-dropdown-item>
          </b-dropdown>
          <b-button
            type="is-info"
            outlined
            @click="
              loadActiveTabData();
              pifilterMapErrors ||
              pifilterMapSuppItemNbr ||
              pifilterMapProductNoun ||
              pifilterMapReworkItems
                ? (showCustomFilterAlert = true)
                : '';
            "
            class="item-level-apply-button"
          >
            Apply
          </b-button>
          <b-button
            type="is-info"
            outlined
            @click="
              resetFilterSortSearchInputs();
              showCustomFilterAlert = false;
            "
            class="item-level-reset-button"
          >
            Reset
          </b-button>
        </div>
        <div class="ript-tab-filter" v-show="activeTab === 2">
          <b-dropdown
            aria-role="menu"
            trap-focus
            class="eu-filter-only-wrapper"
          >
            <button class="button is-info is-outlined" slot="trigger">
              <span>Filter By</span>
            </button>
            <b-dropdown-item aria-role="menu-item" :focusable="false" custom>
              <b-field label="Errored Items">
                <b-select v-model="riptfilterMapErrors">
                  <option value="YES">YES</option>
                  <option value="NO">NO</option>
                </b-select>
              </b-field>
              <b-field label="Supplier Item No."
                ><b-input
                  type="text"
                  :value="riptfilterMapSuppItemNbr"
                  @change.native="
                    riptfilterMapSuppItemNbr = $event.target.value
                  "
                ></b-input
              ></b-field>
              <b-field label="English Product Noun"
                ><b-input
                  type="text"
                  :value="riptfilterMapProductNoun"
                  @change.native="
                    riptfilterMapProductNoun = $event.target.value
                  "
                ></b-input
              ></b-field>
              <b-field label="Is item CE Marked">
                <b-select v-model="riptfilterMapItemCeMarked">
                  <option value="YES">YES</option>
                  <option value="NO">NO</option>
                </b-select>
              </b-field>
              <b-field label="Item Status - Rework – Additional Data Required"
                ><b-checkbox
                  size="is-large"
                  type="is-info"
                  v-model="riptfilterMapReworkItems"
                ></b-checkbox
              ></b-field>
            </b-dropdown-item>
          </b-dropdown>
          <b-dropdown
            aria-role="menu"
            trap-focus
            class="eu-filter-sort-wrapper"
          >
            <button class="button is-info is-outlined" slot="trigger">
              <span>Sort By</span>
            </button>
            <b-dropdown-item aria-role="menu-item" :focusable="false" custom>
              <b-field label="Supplier Item No. Order">
                <b-select v-model="riptsortMapSuppItemNbrOrder">
                  <option value="asc"> Ascending </option
                  ><option value="desc">Descending</option>
                </b-select>
              </b-field>
            </b-dropdown-item>
          </b-dropdown>
          <b-dropdown
            aria-role="menu"
            trap-focus
            class="eu-filter-search-wrapper"
          >
            <button class="button is-info is-outlined" slot="trigger">
              <span>Search By</span>
            </button>
            <b-dropdown-item aria-role="menu-item" :focusable="false" custom>
              <b-field label="Field Name">
                <b-select v-model="riptsearchMapFieldName">
                  <option value="ErroredItems">Errored Items</option
                  ><option value="suppItemNbr">Supplier Item No.</option>
                </b-select>
              </b-field>
              <b-field
                label="Value"
                v-show="riptsearchMapFieldName === 'ErroredItems'"
              >
                <b-select v-model="riptsearchMapInput">
                  <option value="YES">YES</option>
                  <option value="NO">NO</option>
                </b-select>
              </b-field>
              <b-field
                label="Search"
                v-show="riptsearchMapFieldName === 'suppItemNbr'"
              >
                <b-input
                  type="text"
                  v-model="riptsearchMapInput"
                  @change.native="riptsearchMapInput = $event.target.value"
                ></b-input>
              </b-field>
            </b-dropdown-item>
          </b-dropdown>
          <b-button
            type="is-info"
            outlined
            @click="
              loadActiveTabData();
              riptfilterMapErrors ||
              riptfilterMapSuppItemNbr ||
              riptfilterMapProductNoun ||
              riptfilterMapItemCeMarked ||
              riptfilterMapReworkItems
                ? (showCustomFilterAlert = true)
                : '';
            "
            class="item-level-apply-button"
          >
            Apply
          </b-button>
          <b-button
            type="is-info"
            outlined
            @click="
              resetFilterSortSearchInputs();
              showCustomFilterAlert = false;
            "
            class="item-level-reset-button"
          >
            Reset
          </b-button>
        </div>
        <div class="ricb-tab-filter" v-show="activeTab === 3">
          <b-dropdown
            aria-role="menu"
            trap-focus
            class="eu-filter-only-wrapper"
          >
            <button class="button is-info is-outlined" slot="trigger">
              <span>Filter By</span>
            </button>
            <b-dropdown-item aria-role="menu-item" :focusable="false" custom>
              <b-field label="Errored Items">
                <b-select v-model="ricbfilterMapErrors">
                  <option value="YES">YES</option>
                  <option value="NO">NO</option>
                </b-select>
              </b-field>
              <b-field label="Supplier Item No."
                ><b-input
                  type="text"
                  :value="ricbfilterMapSuppItemNbr"
                  @change.native="
                    ricbfilterMapSuppItemNbr = $event.target.value
                  "
                ></b-input
              ></b-field>
              <b-field label="English Product Noun"
                ><b-input
                  type="text"
                  :value="ricbfilterMapProductNoun"
                  @change.native="
                    ricbfilterMapProductNoun = $event.target.value
                  "
                ></b-input
              ></b-field>
              <b-field label="Is it hazardous according to CLP">
                <b-select v-model="ricbfilterMapDangGood">
                  <option value="YES">YES</option>
                  <option value="NO">NO</option>
                </b-select>
              </b-field>
              <b-field label="Item Status - Rework – Additional Data Required"
                ><b-checkbox
                  size="is-large"
                  type="is-info"
                  v-model="ricbfilterMapReworkItems"
                ></b-checkbox
              ></b-field>
            </b-dropdown-item>
          </b-dropdown>
          <b-dropdown
            aria-role="menu"
            trap-focus
            class="eu-filter-sort-wrapper"
          >
            <button class="button is-info is-outlined" slot="trigger">
              <span>Sort By</span>
            </button>
            <b-dropdown-item aria-role="menu-item" :focusable="false" custom>
              <b-field label="Supplier Item No. Order">
                <b-select v-model="ricbsortMapSuppItemNbrOrder">
                  <option value="asc"> Ascending </option
                  ><option value="desc">Descending</option>
                </b-select>
              </b-field>
            </b-dropdown-item>
          </b-dropdown>
          <b-dropdown
            aria-role="menu"
            trap-focus
            class="eu-filter-search-wrapper"
          >
            <button class="button is-info is-outlined" slot="trigger">
              <span>Search By</span>
            </button>
            <b-dropdown-item aria-role="menu-item" :focusable="false" custom>
              <b-field label="Field Name">
                <b-select v-model="ricbsearchMapFieldName">
                  <option value="ErroredItems">Errored Items</option
                  ><option value="suppItemNbr">Supplier Item No.</option>
                </b-select>
              </b-field>
              <b-field
                label="Value"
                v-show="ricbsearchMapFieldName === 'ErroredItems'"
              >
                <b-select v-model="ricbsearchMapInput">
                  <option value="YES">YES</option>
                  <option value="NO">NO</option>
                </b-select>
              </b-field>
              <b-field
                label="Search"
                v-show="ricbsearchMapFieldName === 'suppItemNbr'"
              >
                <b-input
                  type="text"
                  v-model="ricbsearchMapInput"
                  @change.native="ricbsearchMapInput = $event.target.value"
                ></b-input>
              </b-field>
            </b-dropdown-item>
          </b-dropdown>
          <b-button
            type="is-info"
            outlined
            @click="
              loadActiveTabData();
              ricbfilterMapErrors ||
              ricbfilterMapSuppItemNbr ||
              ricbfilterMapProductNoun ||
              ricbfilterMapDangGood ||
              ricbfilterMapReworkItems
                ? (showCustomFilterAlert = true)
                : '';
            "
            class="item-level-apply-button"
          >
            Apply
          </b-button>
          <b-button
            type="is-info"
            outlined
            @click="
              resetFilterSortSearchInputs();
              showCustomFilterAlert = false;
            "
            class="item-level-reset-button"
          >
            Reset
          </b-button>
        </div>
        <div class="riei-tab-filter" v-show="activeTab === 4">
          <b-dropdown
            aria-role="menu"
            trap-focus
            class="eu-filter-only-wrapper"
          >
            <button class="button is-info is-outlined" slot="trigger">
              <span>Filter By</span>
            </button>
            <b-dropdown-item aria-role="menu-item" :focusable="false" custom>
              <b-field label="Errored Items">
                <b-select v-model="rieifilterMapErrors">
                  <option value="YES">YES</option>
                  <option value="NO">NO</option>
                </b-select>
              </b-field>
              <b-field label="Supplier Item No."
                ><b-input
                  type="text"
                  :value="rieifilterMapSuppItemNbr"
                  @change.native="
                    rieifilterMapSuppItemNbr = $event.target.value
                  "
                ></b-input
              ></b-field>
              <b-field label="English Product Noun"
                ><b-input
                  type="text"
                  :value="rieifilterMapProductNoun"
                  @change.native="
                    rieifilterMapProductNoun = $event.target.value
                  "
                ></b-input
              ></b-field>
              <b-field label="Item Status - Rework – Additional Data Required"
                ><b-checkbox
                  size="is-large"
                  type="is-info"
                  v-model="rieifilterMapReworkItems"
                ></b-checkbox
              ></b-field>
            </b-dropdown-item>
          </b-dropdown>
          <b-dropdown
            aria-role="menu"
            trap-focus
            class="eu-filter-sort-wrapper"
          >
            <button class="button is-info is-outlined" slot="trigger">
              <span>Sort By</span>
            </button>
            <b-dropdown-item aria-role="menu-item" :focusable="false" custom>
              <b-field label="Supplier Item No. Order">
                <b-select v-model="rieisortMapSuppItemNbrOrder">
                  <option value="asc"> Ascending </option
                  ><option value="desc">Descending</option>
                </b-select>
              </b-field>
            </b-dropdown-item>
          </b-dropdown>
          <b-dropdown
            aria-role="menu"
            trap-focus
            class="eu-filter-search-wrapper"
          >
            <button class="button is-info is-outlined" slot="trigger">
              <span>Search By</span>
            </button>
            <b-dropdown-item aria-role="menu-item" :focusable="false" custom>
              <b-field label="Field Name">
                <b-select v-model="rieisearchMapFieldName">
                  <option value="ErroredItems">Errored Items</option
                  ><option value="suppItemNbr">Supplier Item No.</option>
                </b-select>
              </b-field>
              <b-field
                label="Value"
                v-show="rieisearchMapFieldName === 'ErroredItems'"
              >
                <b-select v-model="rieisearchMapInput">
                  <option value="YES">YES</option>
                  <option value="NO">NO</option>
                </b-select>
              </b-field>
              <b-field
                label="Search"
                v-show="rieisearchMapFieldName === 'suppItemNbr'"
              >
                <b-input
                  type="text"
                  v-model="rieisearchMapInput"
                  @change.native="rieisearchMapInput = $event.target.value"
                ></b-input>
              </b-field>
            </b-dropdown-item>
          </b-dropdown>
          <b-button
            type="is-info"
            outlined
            @click="
              loadActiveTabData();
              rieifilterMapErrors ||
              rieifilterMapSuppItemNbr ||
              rieifilterMapProductNoun ||
              rieifilterMapReworkItems
                ? (showCustomFilterAlert = true)
                : '';
            "
            class="item-level-apply-button"
          >
            Apply
          </b-button>
          <b-button
            type="is-info"
            outlined
            @click="
              resetFilterSortSearchInputs();
              showCustomFilterAlert = false;
            "
            class="item-level-reset-button"
          >
            Reset
          </b-button>
        </div>
      </div>
      <div class="eu-item-level-btn-wrapper">
        <b-button
          type="is-danger"
          outlined
          class="eu-show-errors"
          title="Show Errors"
          v-show="
            showItemDetail &&
              ((activeTab === 0 && giErrorCountOnTabOpen > 0) ||
                (activeTab === 1 && piErrorCountOnTabOpen > 0) ||
                (activeTab === 2 && riptErrorCountOnTabOpen > 0) ||
                (activeTab === 3 && ricbErrorCountOnTabOpen > 0) ||
                (activeTab === 4 && rieiErrorCountOnTabOpen > 0))
          "
          @click="showErrorsByTabIndex"
          >Show Errors</b-button
        >
        <b-button
          v-show="showItemDetail"
          @click="
            showItemDetail = false;
            showSubmissionDetail = true;
            loadSubmissionDetails();
          "
          type="is-info"
          class="view-form-item"
          >View Submission Detail</b-button
        >
        <b-button
          v-show="showSubmissionDetail"
          @click="
            showSubmissionDetail = false;
            showItemDetail = true;
            loadEUGeneralInfoTab();
          "
          type="is-info"
          class="view-form-item"
          >View Item Level Detail</b-button
        >
        <b-dropdown
          :triggers="['hover']"
          aria-role="list"
          class="eu-upload-tab-data"
          v-show="
            showItemDetail &&
              !euEventType.viewRequest &&
              !euEventType.reViewRequest
          "
        >
          <template #trigger>
            <b-button
              label="Upload Tab Data"
              type="is-info"
              icon-right="menu-down"
            />
          </template>

          <b-dropdown-item aria-role="listitem"
            ><b-upload v-model="tabUploadFile" @input="uploadTabData('GI')">
              <a
                title="This process can take up to 10 minutes when the number of records are 1000 or more."
              >
                <span>General Information</span>
              </a>
            </b-upload></b-dropdown-item
          >
          <b-dropdown-item aria-role="listitem"
            ><b-upload v-model="tabUploadFile" @input="uploadTabData('PI')">
              <a
                title="This process can take up to 10 minutes when the number of records are 1000 or more."
              >
                <span>Price Information</span>
              </a>
            </b-upload></b-dropdown-item
          >
          <b-dropdown-item aria-role="listitem"
            ><b-upload v-model="tabUploadFile" @input="uploadTabData('RIPT')">
              <a
                title="This process can take up to 10 minutes when the number of records are 1000 or more."
              >
                <span>Regulatory Information - Product Types</span>
              </a>
            </b-upload></b-dropdown-item
          >
          <b-dropdown-item aria-role="listitem"
            ><b-upload v-model="tabUploadFile" @input="uploadTabData('RICB')">
              <a
                title="This process can take up to 10 minutes when the number of records are 1000 or more."
              >
                <span>Regulatory Information - Chemical and Biological</span>
              </a>
            </b-upload></b-dropdown-item
          >
          <b-dropdown-item aria-role="listitem"
            ><b-upload v-model="tabUploadFile" @input="uploadTabData('RIEI')">
              <a
                title="This process can take up to 10 minutes when the number of records are 1000 or more."
              >
                <span>Regulatory Information - Equipment and Instrument</span>
              </a>
            </b-upload></b-dropdown-item
          >
        </b-dropdown>
        <b-dropdown
          :triggers="['hover']"
          aria-role="list"
          class="eu-download-tab-data"
          v-show="showItemDetail"
        >
          <template #trigger>
            <b-button
              label="Download Tab Data"
              type="is-info"
              icon-right="menu-down"
            />
          </template>

          <b-dropdown-item aria-role="listitem">
            <a
              title="This process can take up to 10 minutes when the number of records are 1000 or more."
              @click="downloadTabData('GI')"
            >
              <span>General Information</span>
            </a>
          </b-dropdown-item>
          <b-dropdown-item aria-role="listitem">
            <a
              title="This process can take up to 10 minutes when the number of records are 1000 or more."
              @click="downloadTabData('PI')"
            >
              <span>Price Information</span>
            </a>
          </b-dropdown-item>
          <b-dropdown-item aria-role="listitem">
            <a
              title="This process can take up to 10 minutes when the number of records are 1000 or more."
              @click="downloadTabData('RIPT')"
            >
              <span>Regulatory Information - Product Types</span>
            </a>
          </b-dropdown-item>
          <b-dropdown-item aria-role="listitem">
            <a
              title="This process can take up to 10 minutes when the number of records are 1000 or more."
              @click="downloadTabData('RICB')"
            >
              <span>Regulatory Information - Chemical and Biological</span>
            </a>
          </b-dropdown-item>
          <b-dropdown-item aria-role="listitem">
            <a
              title="This process can take up to 10 minutes when the number of records are 1000 or more."
              @click="downloadTabData('RIEI')"
            >
              <span>Regulatory Information - Equipment and Instrument</span>
            </a>
          </b-dropdown-item>
        </b-dropdown>
        <b-dropdown
          :triggers="['hover']"
          aria-role="list"
          class="eu-upload-attachemnts"
          v-show="
            showItemDetail &&
              !euEventType.viewRequest &&
              !euEventType.reViewRequest &&
              (activeTab === 2 || activeTab === 3)
          "
        >
          <template #trigger>
            <b-button
              label="Upload/Delete Attachment(s)"
              type="is-info"
              icon-right="menu-down"
            />
          </template>

          <b-dropdown-item aria-role="listitem"
            ><b-upload
              v-model="uploadByFilterFile"
              @input="uploadAttachmentByFilter(activeTab)"
            >
              <a
                title="This process can take up to 10 minutes when the number of records are 1000 or more."
              >
                <span>Upload Attachment - Filter</span>
              </a>
            </b-upload></b-dropdown-item
          >
          <b-dropdown-item aria-role="listitem"
            ><b-upload
              v-model="uploadBySelectedItemsFile"
              @input="uploadAttachmentBySelectedItems(activeTab)"
            >
              <a
                title="This process allows upload on a maximum of 100 records per page."
              >
                <span>Upload Attachment - Selected Items</span>
              </a>
            </b-upload></b-dropdown-item
          >
          <b-dropdown-item aria-role="listitem"
            ><b-upload
              v-model="uploadToTotalSubmissionFile"
              @input="uploadAttachmentToTotalSubmission(activeTab)"
            >
              <a
                title="This process can take up to 10 minutes when the number of records are 1000 or more."
              >
                <span>Upload Attachment - Total Submission</span>
              </a>
            </b-upload></b-dropdown-item
          >
          <b-dropdown-item aria-role="listitem" class="delete-file"
            ><b-button @click="deleteAttachmentByFilter(activeTab)">
              <a
                title="This process can take up to 10 minutes when the number of records are 1000 or more."
              >
                <span>Delete Attachment - Filter</span>
              </a>
            </b-button></b-dropdown-item
          >
          <b-dropdown-item aria-role="listitem" class="delete-file"
            ><b-button @click="deleteAttachmentBySelectedItems(activeTab)">
              <a
                title="This process allows delete on a maximum of 100 records per page."
              >
                <span>Delete Attachment - Selected Items</span>
              </a>
            </b-button></b-dropdown-item
          >
          <b-dropdown-item aria-role="listitem" class="delete-file"
            ><b-button @click="deleteAttachmentToTotalSubmission(activeTab)">
              <a
                title="This process can take up to 10 minutes when the number of records are 1000 or more."
              >
                <span>Delete Attachment - Total Submission</span>
              </a>
            </b-button></b-dropdown-item
          >
        </b-dropdown>
      </div>
      <b-button
        type="is-danger"
        outlined
        class="custom-filter-applied"
        v-show="showCustomFilterAlert"
      >
        {{
          gifilterMapErrors ||
          gifilterMapSuppItemNbr ||
          gifilterMapProductNoun ||
          gifilterMapProdCtgry ||
          gifilterMapReworkItems ||
          pifilterMapErrors ||
          pifilterMapSuppItemNbr ||
          pifilterMapProductNoun ||
          pifilterMapReworkItems ||
          riptfilterMapErrors ||
          riptfilterMapSuppItemNbr ||
          riptfilterMapProductNoun ||
          riptfilterMapItemCeMarked ||
          riptfilterMapReworkItems ||
          ricbfilterMapErrors ||
          ricbfilterMapSuppItemNbr ||
          ricbfilterMapProductNoun ||
          ricbfilterMapDangGood ||
          ricbfilterMapReworkItems ||
          rieifilterMapErrors ||
          rieifilterMapSuppItemNbr ||
          rieifilterMapProductNoun ||
          rieifilterMapReworkItems
            ? "Custom Filter Applied"
            : ""
        }}
      </b-button>
    </div>
    <div class="ni-app-submission-form eu-region">
      <section
        class="modal-card-body ni-submission-level-detail-body eu-region"
        :class="
          euEventType.viewRequest ||
          euEventType.reViewRequest ||
          (euEventType.editRequest && this.isReworkItem)
            ? 'viewOnly'
            : ''
        "
        v-show="showSubmissionDetail"
      >
        <div
          class="submitter-only-wrapper eu-region"
          v-on:change="saveEuSubmission"
        >
          <div class="columns">
            <ValidationProvider
              rules="required"
              :bails="false"
              v-slot="{ classes }"
              immediate
              class="column view-always"
            >
              <b-field
                label="Supplier Number"
                label-position="on-border"
                :class="classes"
                title="Unique account number assigned to the supplier of the product"
              >
                <b-input type="text" :value="suppNbr" disabled class="vn-name">
                </b-input>
              </b-field>
            </ValidationProvider>
            <ValidationProvider
              rules="required"
              :bails="false"
              v-slot="{ classes }"
              immediate
              class="column"
            >
              <b-field
                label="Supplier Name"
                label-position="on-border"
                :class="classes"
                title="Primary supplier of the item; name that appears on purchase orders, invoices, etc."
              >
                <b-input type="text" :value="suppName" disabled class="vn-name">
                </b-input>
              </b-field>
            </ValidationProvider>
            <ValidationProvider
              rules="required"
              :bails="false"
              v-slot="{ classes }"
              immediate
              class="column"
            >
              <b-field
                label="Web Site"
                label-position="on-border"
                :class="classes"
                title="Internet address of the supplier (i.e.www.fishersci.com)"
              >
                <b-input type="text" v-model="website"> </b-input>
              </b-field>
            </ValidationProvider>
          </div>
          <div class="columns">
            <ValidationProvider
              rules="required"
              :bails="false"
              v-slot="{ classes }"
              immediate
              class="column"
            >
              <b-field
                label="Submission Description"
                label-position="on-border"
                class="is-6"
                :class="classes"
                title="Description of the submission to be used for the supplier/Fisher to identify the submission easily."
              >
                <b-input
                  oninput="if (this.value.length > 200)
                        this.value = this.value.slice(0, 200);"
                  type="textarea"
                  v-model="submissionDesc"
                >
                </b-input>
              </b-field>
            </ValidationProvider>
            <div class="column">
              <b-field
                label="Created Date"
                label-position="on-border"
                class="column"
                title="Date the submission was created"
              >
                <b-input type="text" v-model="createdDate" disabled="true">
                </b-input>
              </b-field>
              <b-field
                label="Created By"
                label-position="on-border"
                class="column"
                title="Created By"
              >
                <b-input type="text" v-model="createdBy" disabled="true">
                </b-input>
              </b-field>
            </div>
          </div>
          <div class="columns">
            <ValidationProvider
              rules="required"
              :bails="false"
              v-slot="{ classes }"
              immediate
              class="column"
            >
              <div class="control" :class="classes">
                <b-field
                  label="Product Contact Name"
                  label-position="on-border"
                  title="The name of the supplier representative to contact regarding product information (usually the person who completes this form)"
                >
                  <b-input type="text" v-model="prodContactName"> </b-input>
                </b-field>
              </div>
            </ValidationProvider>
            <ValidationProvider
              rules="required"
              :bails="false"
              v-slot="{ classes }"
              immediate
              class="column"
            >
              <div class="control" :class="classes">
                <b-field
                  label="Product Contact Email Address"
                  label-position="on-border"
                  title="The email of the supplier representative to contact regarding product information (usually the person who completes this form)"
                >
                  <b-input type="email" v-model="prodContactEmail"> </b-input>
                </b-field>
              </div>
            </ValidationProvider>
            <ValidationProvider
              rules="required"
              :bails="false"
              v-slot="{ classes }"
              immediate
              class="column"
            >
              <div class="control" :class="classes">
                <b-field label="Product Launch Date" label-position="on-border">
                  <b-datepicker
                    v-model="launchDate"
                    :min-date="minDate"
                    @input="saveEuSubmission"
                  >
                  </b-datepicker>
                </b-field>
              </div>
            </ValidationProvider>
          </div>
          <div class="columns">
            <ValidationProvider
              rules="required"
              :bails="false"
              v-slot="{ classes }"
              immediate
              class="column"
            >
              <div class="control" :class="classes">
                <b-field
                  label="Regulatory Contact Name"
                  label-position="on-border"
                  title="The name of the supplier representative to contact regarding regulatory product information"
                >
                  <b-input type="text" v-model="regContactName"> </b-input>
                </b-field>
              </div>
            </ValidationProvider>
            <ValidationProvider
              rules="required"
              :bails="false"
              v-slot="{ classes }"
              immediate
              class="column"
            >
              <div class="control" :class="classes">
                <b-field
                  label="Regulatory Contact Phone Number"
                  label-position="on-border"
                  title="The phone number of the supplier representative to contact regarding regulatory product information"
                >
                  <b-input type="telephone" v-model="regContactPhone">
                  </b-input>
                </b-field>
              </div>
            </ValidationProvider>
            <ValidationProvider
              rules="required"
              :bails="false"
              v-slot="{ classes }"
              immediate
              class="column"
            >
              <div class="control" :class="classes">
                <b-field
                  label="Regulatory Contact Email Address"
                  label-position="on-border"
                  title="The email of the supplier representative to contact regarding regulatory product information"
                >
                  <b-input type="email" v-model="regContactEmail"> </b-input>
                </b-field>
              </div>
            </ValidationProvider>
          </div>
        </div>
        <div class="columns submission-file-wrapper">
          <h6 class="file-title">Submission Attachment</h6>
          <a
            v-if="submissionAttachmentName"
            class="submission-file has-text-info file-content"
          >
            <span
              :title="submissionAttachmentName"
              class="attachment-title download-attachment"
              >{{ submissionAttachmentName }}
            </span>
            <a
              class="download-submission-attachment"
              title="Click to download attachment"
              @click="downloadSubmissionAttachment()"
              ><b-icon icon="download" type="is-info" size="is-small"> </b-icon
            ></a>
            <a
              @click="deleteSubmissionAttachment()"
              class="delete-attachment"
              title="Delete Attachment"
            >
              <b-icon icon="delete" type="is-info" size="is-small"> </b-icon
            ></a>
          </a>
          <b-field class="file file-content is-info" v-else>
            <b-upload
              v-model="submissionAttachment"
              class="file-label upload-submission-attachment is-info"
              @input="uploadSubmissionAttachment()"
            >
              <span class="file-cta">
                <b-icon class="file-icon" icon="upload"></b-icon>
                <span class="file-label">Click to upload</span>
              </span>
            </b-upload>
          </b-field>
        </div>
        <div class="eu-submission-files">
          <h6 class="file-title">Submission Files</h6>
          <b-button
            label="Add Cross-Reference Information"
            type="is-info"
            icon-left="file-document-outline"
          />
          <b-button
            class="margin-left-20"
            label="Add Product Accessories"
            type="is-info"
            icon-left="playlist-plus"
          />
        </div>
      </section>
      <section
        class="ni-item-level-submission-detail eu-region"
        v-show="showItemDetail"
      >
        <div
          class="pagination-wrapper gi-eu-pagination-wrapper"
          v-if="activeTab === 0"
        >
          <div class="pagination-header">
            <b-button
              v-show="giShowPreviousButton"
              @click="
                giPageIndex = giPageIndex - 1;
                loadEUGeneralInfoTab();
              "
              >Previous</b-button
            >
            <b-button
              v-show="giShowFirstButton"
              @click="
                giPageIndex = 1;
                loadEUGeneralInfoTab();
              "
              >First</b-button
            >
            <span class="page-index"
              >{{ giMinPageIndex }} to {{ giMaxPageIndex }} of
              {{ giTotalResults }}</span
            >
            <b-button
              v-show="giShowLastButton"
              @click="
                giPageIndex = giTotalPages;
                loadEUGeneralInfoTab();
              "
              >Last</b-button
            >
            <b-button
              v-show="giShowNextButton"
              @click="
                giPageIndex = giPageIndex + 1;
                loadEUGeneralInfoTab();
              "
              >Next</b-button
            >
            <span class="page-navigator" v-show="giTotalResults > 10"
              ><span class="show-text">Show</span
              ><b-select
                class="page-display-number"
                v-model="giNumberOfItems"
                @input="
                  giPageIndex =
                    Math.floor(giMinPageIndex / giNumberOfItems) + 1;
                  loadEUGeneralInfoTab();
                "
                ><option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </b-select></span
            >
          </div>
        </div>
        <div
          class="pagination-wrapper pi-eu-pagination-wrapper"
          v-else-if="activeTab === 1"
        >
          <div class="pagination-header">
            <b-button
              v-show="piShowPreviousButton"
              @click="
                piPageIndex = piPageIndex - 1;
                loadEUPriceInfoTab();
              "
              >Previous</b-button
            >
            <b-button
              v-show="piShowFirstButton"
              @click="
                piPageIndex = 1;
                loadEUPriceInfoTab();
              "
              >First</b-button
            >
            <span class="page-index"
              >{{ piMinPageIndex }} to {{ piMaxPageIndex }} of
              {{ piTotalResults }}</span
            >
            <b-button
              v-show="piShowLastButton"
              @click="
                piPageIndex = piTotalPages;
                loadEUPriceInfoTab();
              "
              >Last</b-button
            >
            <b-button
              v-show="piShowNextButton"
              @click="
                piPageIndex = piPageIndex + 1;
                loadEUPriceInfoTab();
              "
              >Next</b-button
            >
            <span class="page-navigator" v-show="piTotalResults > 10"
              ><span class="show-text">Show</span
              ><b-select
                class="page-display-number"
                v-model="piNumberOfItems"
                @input="
                  piPageIndex =
                    Math.floor(piMinPageIndex / piNumberOfItems) + 1;
                  loadEUPriceInfoTab();
                "
                ><option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </b-select></span
            >
          </div>
        </div>
        <div
          class="pagination-wrapper ript-eu-pagination-wrapper"
          v-if="activeTab === 2"
        >
          <div class="pagination-header">
            <b-button
              v-show="riPtShowPreviousButton"
              @click="
                riPtPageIndex = riPtPageIndex - 1;
                loadEURegulatoryInfoProductTypesTab();
              "
              >Previous</b-button
            >
            <b-button
              v-show="riPtShowFirstButton"
              @click="
                riPtPageIndex = 1;
                loadEURegulatoryInfoProductTypesTab();
              "
              >First</b-button
            >
            <span class="page-index"
              >{{ riPtMinPageIndex }} to {{ riPtMaxPageIndex }} of
              {{ riPtTotalResults }}</span
            >
            <b-button
              v-show="riPtShowLastButton"
              @click="
                riPtPageIndex = riPtTotalPages;
                loadEURegulatoryInfoProductTypesTab();
              "
              >Last</b-button
            >
            <b-button
              v-show="riPtShowLastButton"
              @click="
                riPtPageIndex = riPtPageIndex + 1;
                loadEURegulatoryInfoProductTypesTab();
              "
              >Next</b-button
            >
            <span class="page-navigator" v-show="riPtTotalResults > 10"
              ><span class="show-text">Show</span
              ><b-select
                class="page-display-number"
                v-model="riPtNumberOfItems"
                @input="
                  riPtPageIndex =
                    Math.floor(riPtMinPageIndex / riPtNumberOfItems) + 1;
                  loadEURegulatoryInfoProductTypesTab();
                "
                ><option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </b-select></span
            >
          </div>
        </div>
        <div
          class="pagination-wrapper ricb-eu-pagination-wrapper"
          v-if="activeTab === 3"
        >
          <div class="pagination-header">
            <b-button
              v-show="riCbShowPreviousButton"
              @click="
                riCbPageIndex = riCbPageIndex - 1;
                loadEURegulatoryInfoChemicalBiologicalTab();
              "
              >Previous</b-button
            >
            <b-button
              v-show="riCbShowFirstButton"
              @click="
                riCbPageIndex = 1;
                loadEURegulatoryInfoChemicalBiologicalTab();
              "
              >First</b-button
            >
            <span class="page-index"
              >{{ riCbMinPageIndex }} to {{ riCbMaxPageIndex }} of
              {{ riCbTotalResults }}</span
            >
            <b-button
              v-show="riCbShowLastButton"
              @click="
                riCbPageIndex = riCbTotalPages;
                loadEURegulatoryInfoChemicalBiologicalTab();
              "
              >Last</b-button
            >
            <b-button
              v-show="riCbShowNextButton"
              @click="
                riCbPageIndex = riCbPageIndex + 1;
                loadEURegulatoryInfoChemicalBiologicalTab();
              "
              >Next</b-button
            >
            <span class="page-navigator" v-show="riCbTotalResults > 10"
              ><span class="show-text">Show</span
              ><b-select
                class="page-display-number"
                v-model="riCbNumberOfItems"
                @input="
                  pageIndex =
                    Math.floor(riCbMinPageIndex / riCbNumberOfItems) + 1;
                  loadEURegulatoryInfoChemicalBiologicalTab();
                "
                ><option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </b-select></span
            >
          </div>
        </div>
        <div
          class="pagination-wrapper riei-eu-pagination-wrapper"
          v-if="activeTab === 4"
        >
          <div class="pagination-header">
            <b-button
              v-show="riEiShowPreviousButton"
              @click="
                riEiPageIndex = riEiPageIndex - 1;
                loadEURegulatoryInfoEquipmentInstrumentTab();
              "
              >Previous</b-button
            >
            <b-button
              v-show="riEiShowFirstButton"
              @click="
                riEiPageIndex = 1;
                loadEURegulatoryInfoEquipmentInstrumentTab();
              "
              >First</b-button
            >
            <span class="page-index"
              >{{ riEiMinPageIndex }} to {{ riEiMaxPageIndex }} of
              {{ riEiTotalResults }}</span
            >
            <b-button
              v-show="riEiShowLastButton"
              @click="
                riEiPageIndex = riEiTotalPages;
                loadEURegulatoryInfoEquipmentInstrumentTab();
              "
              >Last</b-button
            >
            <b-button
              v-show="riEiShowNextButton"
              @click="
                riEiPageIndex = riEiPageIndex + 1;
                loadEURegulatoryInfoEquipmentInstrumentTab();
              "
              >Next</b-button
            >
            <span class="page-navigator" v-show="riEiTotalResults > 10"
              ><span class="show-text">Show</span
              ><b-select
                class="page-display-number"
                v-model="riEiNumberOfItems"
                @input="
                  riEiPageIndex =
                    Math.floor(riEiMinPageIndex / riEiNumberOfItems) + 1;
                  loadEURegulatoryInfoEquipmentInstrumentTab();
                "
                ><option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </b-select></span
            >
          </div>
        </div>
        <div class="tabs-wrapper eu-tabs-wrapper" :class="validateTabHeaders()">
          <b-tabs
            class="eu-b-tabs"
            :class="isReworkItem ? 'rework-items-editable' : ''"
            v-model="activeTab"
            type="is-toggle is-info"
            expanded
          >
            <b-tab-item
              label="General Information"
              class="ni-app-general-info-tab"
            >
              <GeneralInfoTab
                :class="[
                  euEventType.viewRequest ||
                  euEventType.reViewRequest ||
                  (euEventType.editRequest && this.isReworkItem)
                    ? 'viewOnly'
                    : ''
                ]"
                :onAddNewRow="triggerAdd"
                :deleteItemsComplete="(rowIds = [])"
                :giTabSubmissionID="euSubmissionID"
                :loadGITabData="canLoadGIData"
                @disableSupplierValidateFromTab="disableSuppValidateBtn = true"
                @enableSupplierValidateFromTab="disableSuppValidateBtn = false"
                @giPaginationParams="updateGIPaginationWrapper"
                @giCount="validateSupplierSubmit"
                @setNewRowToFalse="triggerAdd = false"
              ></GeneralInfoTab>
            </b-tab-item>
            <b-tab-item
              label="Price Information"
              class="ni-app-general-info-tab"
            >
              <PriceInfoTab
                :class="
                  euEventType.viewRequest ||
                  euEventType.reViewRequest ||
                  (euEventType.editRequest && this.isReworkItem)
                    ? 'viewOnly'
                    : ''
                "
                :piTabSubmissionID="euSubmissionID"
                :loadPITabData="canLoadPIData"
                @disableSupplierValidateFromTab="disableSuppValidateBtn = true"
                @enableSupplierValidateFromTab="disableSuppValidateBtn = false"
                @piPaginationParams="updatePIPaginationWrapper"
                @piCount="validateSupplierSubmit"
              ></PriceInfoTab>
            </b-tab-item>
            <b-tab-item
              label="Regulatory Information - Product Types"
              class="ni-app-general-info-tab"
            >
              <RegulatoryInfoProductTypesTab
                :class="
                  euEventType.viewRequest ||
                  euEventType.reViewRequest ||
                  (euEventType.editRequest && this.isReworkItem)
                    ? 'viewOnly'
                    : ''
                "
                :riPtTabSubmissionID="euSubmissionID"
                :loadRiPtTabData="canLoadRIPTData"
                :resetCheckboxSelection="resetSelection"
                @disableSupplierValidateFromTab="disableSuppValidateBtn = true"
                @enableSupplierValidateFromTab="disableSuppValidateBtn = false"
                @riPtIdsForUpload="selectIdsForRIPTUpload"
                @riPtPaginationParams="updateRIPTPaginationWrapper"
                @riptCount="validateSupplierSubmit"
              ></RegulatoryInfoProductTypesTab>
            </b-tab-item>
            <b-tab-item
              label="Regulatory Information - Chemical and Biological"
              class="ni-app-general-info-tab"
            >
              <RegulatoryInfoChemicalBiologicalTab
                :class="
                  euEventType.viewRequest ||
                  euEventType.reViewRequest ||
                  (euEventType.editRequest && this.isReworkItem)
                    ? 'viewOnly'
                    : ''
                "
                :riCbTabSubmissionID="euSubmissionID"
                :loadRiCbTabData="canLoadRICBData"
                :resetCheckboxSelection="resetSelection"
                @disableSupplierValidateFromTab="disableSuppValidateBtn = true"
                @enableSupplierValidateFromTab="disableSuppValidateBtn = false"
                @riCbIdsForUpload="selectIdsForRICBUpload"
                @riCbPaginationParams="updateRICBPaginationWrapper"
                @ricbCount="validateSupplierSubmit"
              ></RegulatoryInfoChemicalBiologicalTab>
            </b-tab-item>
            <b-tab-item
              label="Regulatory Information - Equipment and Instrument"
              class="ni-app-general-info-tab"
            >
              <RegulatoryInfoEquipmentInstrumentTab
                :class="
                  euEventType.viewRequest ||
                  euEventType.reViewRequest ||
                  (euEventType.editRequest && this.isReworkItem)
                    ? 'viewOnly'
                    : ''
                "
                :riEiTabSubmissionID="euSubmissionID"
                :loadRiEiTabData="canLoadRIEIData"
                @disableSupplierValidateFromTab="disableSuppValidateBtn = true"
                @enableSupplierValidateFromTab="disableSuppValidateBtn = false"
                @riEiPaginationParams="updateRIEIPaginationWrapper"
                @rieiCount="validateSupplierSubmit"
              ></RegulatoryInfoEquipmentInstrumentTab>
            </b-tab-item>
          </b-tabs>
        </div>
      </section>
    </div>
    <b-modal
      :active.sync="isViewUploadErrorsModalActive"
      has-modal-card
      trap-focus
      aria-role="dialog"
      aria-modal
    >
      <ViewUploadErrorsModal v-bind="errorsModalProps"></ViewUploadErrorsModal>
    </b-modal>
  </div>
</template>

<script>
import BSelect from "buefy/src/components/select/Select";
import BDropdownItem from "buefy/src/components/dropdown/DropdownItem";
import BButton from "buefy/src/components/button/Button";
import BInput from "buefy/src/components/input/Input";
import BDatepicker from "buefy/src/components/datepicker/Datepicker";
import BTabItem from "buefy/src/components/tabs/TabItem";
import { extend } from "vee-validate";
import * as rules from "vee-validate/dist/rules";
import GeneralInfoTab from "../components/eu-tabs-data/GeneralInfoTab.vue";
import PriceInfoTab from "../components/eu-tabs-data/PriceInfoTab.vue";
import RegulatoryInfoProductTypesTab from "../components/eu-tabs-data/RegulatoryInfoProductTypesTab.vue";
import RegulatoryInfoChemicalBiologicalTab from "../components/eu-tabs-data/RegulatoryInfoChemicalBiologicalTab.vue";
import RegulatoryInfoEquipmentInstrumentTab from "../components/eu-tabs-data/RegulatoryInfoEquipmentInstrumentTab";
import ViewUploadErrorsModal from "../components/ViewUploadErrorsModal.vue";

extend("required", {
  validate(value) {
    return {
      required: true,
      valid: [" ", "", null, undefined, 0].indexOf(value) === -1
    };
  },
  computesRequired: true
});

export default {
  name: "EUSubmissionForm",
  components: {
    BSelect,
    // eslint-disable-next-line vue/no-unused-components
    BDropdownItem,
    BButton,
    BInput,
    BDatepicker,
    BTabItem,
    GeneralInfoTab,
    PriceInfoTab,
    RegulatoryInfoChemicalBiologicalTab,
    RegulatoryInfoEquipmentInstrumentTab,
    RegulatoryInfoProductTypesTab,
    ViewUploadErrorsModal
  },
  props: ["euEventType"],
  data() {
    return {
      activeTab: 0,
      canLoadGIData: {
        loadDetails: false,
        // generalQuestionRows: [],
        actions: {},
        paginationParams: {}
      },
      canLoadPIData: {
        loadDetails: false,
        // generalQuestionRows: [],
        actions: {},
        paginationParams: {}
      },
      canLoadRICBData: {
        loadDetails: false,
        // generalQuestionRows: [],
        actions: {},
        paginationParams: {}
      },
      canLoadRIEIData: {
        loadDetails: false,
        // generalQuestionRows: [],
        actions: {},
        paginationParams: {}
      },
      canLoadRIPTData: {
        loadDetails: false,
        // generalQuestionRows: [],
        actions: {},
        paginationParams: {}
      },
      createdDate: "",
      createdBy: "",
      disableSupplierSubmit: true,
      disableSuppValidateBtn: false,
      errorCountOnUpload: "",
      errorsModalProps: {
        region: "eu",
        submissionId: [],
        tab: []
      },
      euSubmissionID: "",
      gifilterMapErrors: "",
      gifilterMapSuppItemNbr: "",
      giSortMapSuppItemNbrOrder: "",
      gifilterMapProdCtgry: "",
      gifilterMapProductNoun: "",
      gifilterMapReworkItems: false,
      gisearchMapFieldName: "",
      gisearchMapInput: "",
      pifilterMapErrors: "",
      pifilterMapSuppItemNbr: "",
      pifilterMapProductNoun: "",
      pifilterMapReworkItems: false,
      pisortMapSuppItemNbrOrder: "",
      pisearchMapFieldName: "",
      pisearchMapInput: "",
      riptfilterMapErrors: "",
      riptfilterMapSuppItemNbr: "",
      riptfilterMapProductNoun: "",
      riptfilterMapItemCeMarked: "",
      riptfilterMapReworkItems: false,
      riptsortMapSuppItemNbrOrder: "",
      riptsearchMapFieldName: "",
      riptsearchMapInput: "",
      ricbfilterMapErrors: "",
      ricbfilterMapSuppItemNbr: "",
      ricbfilterMapProductNoun: "",
      ricbfilterMapDangGood: "",
      ricbfilterMapReworkItems: false,
      ricbsortMapSuppItemNbrOrder: "",
      ricbsearchMapFieldName: "",
      ricbsearchMapInput: "",
      rieifilterMapErrors: "",
      rieifilterMapSuppItemNbr: "",
      rieifilterMapProductNoun: "",
      rieifilterMapReworkItems: false,
      rieisortMapSuppItemNbrOrder: "",
      rieisearchMapFieldName: "",
      rieisearchMapInput: "",
      isLoading: false,
      isReworkItem: false,
      isSupplierReqFieldsValid: false,
      isUser3P: "",
      isViewUploadErrorsModalActive: false,
      launchDate: null,
      minDate: new Date(
        new Date().getFullYear(),
        new Date().getMonth(),
        new Date().getDate()
      ),
      //pagination vars start
      giPageIndex: 1,
      giMinPageIndex: "",
      giMaxPageIndex: "",
      giTotalResults: "",
      giTotalPages: "",
      giShowPreviousButton: true,
      giShowFirstButton: true,
      giShowLastButton: true,
      giShowNextButton: true,
      giNumberOfItems: 10,
      piPageIndex: 1,
      piMinPageIndex: "",
      piMaxPageIndex: "",
      piTotalResults: "",
      piTotalPages: "",
      piShowPreviousButton: true,
      piShowFirstButton: true,
      piShowLastButton: true,
      piShowNextButton: true,
      piNumberOfItems: 10,
      riPtPageIndex: 1,
      riPtMinPageIndex: "",
      riPtMaxPageIndex: "",
      riPtTotalResults: "",
      riPtTotalPages: "",
      riPtShowPreviousButton: true,
      riPtShowFirstButton: true,
      riPtShowLastButton: true,
      riPtShowNextButton: true,
      riPtNumberOfItems: 10,
      riCbPageIndex: 1,
      riCbMinPageIndex: "",
      riCbMaxPageIndex: "",
      riCbTotalResults: "",
      riCbTotalPages: "",
      riCbShowPreviousButton: true,
      riCbShowFirstButton: true,
      riCbShowLastButton: true,
      riCbShowNextButton: true,
      riCbNumberOfItems: 10,
      riEiPageIndex: 1,
      riEiMinPageIndex: "",
      riEiMaxPageIndex: "",
      riEiTotalResults: "",
      riEiTotalPages: "",
      riEiShowPreviousButton: true,
      riEiShowFirstButton: true,
      riEiShowLastButton: true,
      riEiShowNextButton: true,
      riEiNumberOfItems: 10,
      //pagination vars end
      prodContactName: "",
      prodContactEmail: "",
      prodCount: "",
      regContactName: "",
      regContactEmail: "",
      regContactPhone: "",
      resetSelection: false,
      showCustomFilterAlert: false,
      showExitButton: false,
      showInternalVNs: "",
      showItemDetail: false,
      showSubmissionDetail: true,
      showSubmissionForSupplierUser: false,
      showSubmissionInReadOnly: false,
      submissionAttachment: null,
      submissionAttachmentName: "",
      submissionDesc: "",
      submissionStatus: "",
      subGiTabErrorCount: "",
      subPiTabErrorCount: "",
      subRiProdTabErrorCount: "",
      subRiChemTabErrorCount: "",
      subRiEquipTabErrorCount: "",
      giErrorCountOnTabOpen: "",
      piErrorCountOnTabOpen: "",
      riptErrorCountOnTabOpen: "",
      ricbErrorCountOnTabOpen: "",
      rieiErrorCountOnTabOpen: "",
      riptSelectRowIds: [],
      ricbSelectRowIds: [],
      submissionUploadFile: null,
      attachmentsUploadFile: null,
      tabUploadFile: null,
      uploadByFilterFile: null,
      uploadBySelectedItemsFile: null,
      uploadToTotalSubmissionFile: null,
      triggerAdd: false,
      suppNbr: "",
      suppName: "",
      website: ""
    };
  },
  watch: {
    activeTab(val) {
      this.loadActiveTabData();
    },
    euEventType(value) {
      if (value === "createRequestEu") {
        this.isLoading = true;
        this.showSubmissionForSupplierUser = true;
        this.showSubmissionInReadOnly = false;
        this.createNewSubmission();
        this.showExitButton = true;
        this.isReworkItem = false;
      } else if (value.editRequest) {
        this.euSubmissionID = sessionStorage.getItem("editRequestFromSeEu");
        if (sessionStorage.getItem("reworkItem")) {
          this.isReworkItem = true;
          this.showSubmissionInReadOnly = true;
        } else {
          this.isReworkItem = false;
          this.showSubmissionInReadOnly = false;
        }
        this.loadSubmissionDetails();
      } else if (value.viewRequest) {
        this.showSubmissionInReadOnly = true;
        this.isReworkItem = false;
        this.euSubmissionID = sessionStorage.getItem("viewRequestFromSeEu");
        this.loadSubmissionDetails();
      } else if (value.reViewRequest) {
        this.showSubmissionInReadOnly = true;
        this.isReworkItem = false;
        this.euSubmissionID = sessionStorage.getItem("reViewRequestFromSeEu");
        this.loadSubmissionDetails();
      }
    }
  },
  mounted() {},
  methods: {
    cancelSubmission() {
      this.$buefy.dialog.confirm({
        message:
          "This action will cancel the entire submission. Would you like to proceed?",
        cancelText: "No",
        confirmText: "Yes",
        type: "is-success",
        onConfirm: () => this.submitCancelWorkflow("N")
      });
    },
    closeFormAfterSubmit() {
      //TODO update action after My Draft & View All integration
      this.$router.push({ path: "/" });
    },
    createNewSubmission() {
      console.log("Create EU Request");
      var scope = this;
      var args = {};
      args.headers = {
        "content-type": "application/json"
      };
      var requestPayload = {};
      var userData = JSON.parse(sessionStorage.getItem("se_ni_user_eu"));
      requestPayload.createdBy = userData.networkId;
      requestPayload.suppNbr = userData.supplierId;
      requestPayload.suppName = userData.supplierName;
      requestPayload.nameUser = userData.nameUser;

      this.showCreateButtons = true;
      this.showActions = true;

      scope.isLoading = true;
      scope.$http
        .post(
          `${process.env.VUE_APP_API_BASE_DOMAIN}eu/createNewItemSubmission`,
          requestPayload,
          args
        )
        .then(function(response) {
          scope.isLoading = false;
          // scope.showActions = true;
          // var submissionDetails = response.data;
          // scope.from = tableView;
          // scope.submissionStatusIsActive = false;
          // scope.formType = "Create New Submission";
          scope.euSubmissionID = response.data.nbrNewItemCrKey;
          // scope.showCreateButtons = true;
          // scope.showApproveButtons = false;
          // scope.submissionStatusIsDraft = true;
          // scope.showSubmissionForSupplierUser = true;
          // scope.showReworkButton = false;
          scope.$nextTick(function() {
            scope.loadSubmissionDetails();
          });
          sessionStorage.removeItem("createRequestEUFromSe");
        })
        .catch(function(error) {
          scope.isLoading = false;
          sessionStorage.removeItem("createRequestEUFromSe");
          scope.$buefy.dialog.alert({
            message: `A problem occured with Create New Submission. Please clear the cache and try again. If problem persists, please contact the New Item Support at NewItem.IT.Support@thermofisher.com`,
            confirmText: "OK",
            type: "is-danger",
            onConfirm: () => window.close()
          });
        });
    },
    deleteAttachmentByFilter(activeTab) {
      var scope = this;
      if (activeTab === 2) {
        this.$buefy.dialog.confirm({
          message: `Ce Certificate file will be deleted for filtered items only.`,
          confirmText: "OK",
          type: "is-info",
          onConfirm: () => scope.deleteAttachmentFileByFilter(activeTab)
        });
      } else if (activeTab === 3) {
        this.$buefy.dialog.confirm({
          message: `SDS Attach file will be deleted for filtered items only.`,
          confirmText: "OK",
          type: "is-info",
          onConfirm: () => scope.deleteAttachmentFileByFilter(activeTab)
        });
      }
    },
    deleteAttachmentFileByFilter(activeTab) {
      var scope = this;
      var data = {};
      var attachmentName = "";
      var requestPayload = {};
      requestPayload.actions = [];
      requestPayload.filterMap = {};
      requestPayload.searchMap = {};
      requestPayload.sortMap = {};

      if (this.activeTab === 2) {
        //filter for ceCert
        if (
          this.canLoadRIPTData.actions.fErrors ||
          this.canLoadRIPTData.actions.fSuppItemNbr ||
          this.canLoadRIPTData.actions.fProductNoun ||
          this.canLoadRIPTData.actions.fCeMarked ||
          this.canLoadRIPTData.actions.fReworkItem
        ) {
          requestPayload.actions.push("Filter");
          requestPayload.filterMap = {};
          if (this.canLoadRIPTData.actions.fErrors) {
            requestPayload.filterMap.ErroredItems = this.canLoadRIPTData.actions.fErrors;
          }
          if (this.canLoadRIPTData.actions.fSuppItemNbr) {
            requestPayload.filterMap.suppItemNbr = this.canLoadRIPTData.actions.fSuppItemNbr;
          }
          if (this.canLoadRIPTData.actions.fProductNoun) {
            requestPayload.filterMap.englishPrdctNoun = this.canLoadRIPTData.actions.fProductNoun;
          }
          if (this.canLoadRIPTData.actions.fCeMarked) {
            requestPayload.filterMap.itemCeMarked = this.canLoadRIPTData.actions.fCeMarked;
          }
          if (this.canLoadRIPTData.actions.fReworkItem) {
            requestPayload.filterMap.itemStatus = "K";
          }
        }
        if (this.canLoadRICBData.actions.sortSuppItemNbr) {
          requestPayload.actions.push("Sort");
          requestPayload.sortMap = {};
          requestPayload.sortMap.suppItemNbr = this.canLoadRIPTData.actions.sortSuppItemNbr;
        }
        if (
          this.canLoadRICBData.actions.srFieldName &&
          this.canLoadRIPTData.actions.srFieldValue
        ) {
          requestPayload.actions.push("Search");
          requestPayload.searchMap = {};
          requestPayload.searchMap[
            this.canLoadRIPTData.actions.srFieldName
          ] = this.canLoadRIPTData.actions.srFieldValue;
        }
      } else if (this.activeTab === 3) {
        //filter for sdsAttach
        if (
          this.canLoadRICBData.actions.fErrors ||
          this.canLoadRICBData.actions.fSuppItemNbr ||
          this.canLoadRICBData.actions.fProductNoun ||
          this.canLoadRICBData.actions.fDangGood ||
          this.canLoadRICBData.actions.fReworkItem
        ) {
          requestPayload.actions.push("Filter");
          requestPayload.filterMap = {};
          if (this.canLoadRICBData.actions.fErrors) {
            requestPayload.filterMap.ErroredItems = this.canLoadRICBData.actions.fErrors;
          }
          if (this.canLoadRICBData.actions.fSuppItemNbr) {
            requestPayload.filterMap.suppItemNbr = this.canLoadRICBData.actions.fSuppItemNbr;
          }
          if (this.canLoadRICBData.actions.fProductNoun) {
            requestPayload.filterMap.englishPrdctNoun = this.canLoadRICBData.actions.fProductNoun;
          }
          if (this.canLoadRICBData.actions.fDangGood) {
            requestPayload.filterMap.dangGood = this.canLoadRICBData.actions.fDangGood;
          }
          if (this.canLoadRICBData.actions.fReworkItem) {
            requestPayload.filterMap.itemStatus = "K";
          }
        }
        if (this.canLoadRICBData.actions.sortSuppItemNbr) {
          requestPayload.actions.push("Sort");
          requestPayload.sortMap = {};
          requestPayload.sortMap.suppItemNbr = this.canLoadRICBData.actions.sortSuppItemNbr;
        }
        if (
          this.canLoadRICBData.actions.srFieldName &&
          this.canLoadRICBData.actions.srFieldValue
        ) {
          requestPayload.actions.push("Search");
          requestPayload.searchMap = {};
          requestPayload.searchMap[
            this.canLoadRICBData.actions.srFieldName
          ] = this.canLoadRICBData.actions.srFieldValue;
        }
      }
      data.requestPayload = requestPayload;
      if (activeTab === 2) {
        attachmentName = "ceCertificate";
      } else if (activeTab === 3) {
        attachmentName = "sdsAttach";
      }

      scope.isLoading = true;
      scope.$http
        .put(
          `${process.env.VUE_APP_API_BASE_DOMAIN}eu/deleteAttachmentsFilterItems?nbrNewItemCrKey=${scope.euSubmissionID}&attachmentName=${attachmentName}`,
          data
        )
        // eslint-disable-next-line no-unused-vars
        .then(function(response) {
          scope.isLoading = false;
          scope.uploadByFilterFile = null;
          if (response.data.toLowerCase() === "background") {
            scope.$buefy.dialog.alert({
              message: `File is being processed for upload in the background. You will be notified via email once it is complete.`,
              confirmText: "OK",
              type: "is-success",
              onConfirm: () => window.close()
            });
          } else if (response.data.toLowerCase() === "failure") {
            scope.$buefy.dialog.alert({
              message: `A problem occurred with the upload file used. Please remove any special characters or extra line breaks on the description fields and try again. If the problem persists, please contact the IT Support at NewItem.IT.Support@thermofisher.com`,
              confirmText: "OK",
              type: "is-danger"
            });
          } else {
            scope.$buefy.dialog.alert({
              message: `File upload successful.`,
              confirmText: "OK",
              type: "is-success",
              onConfirm: () => scope.loadActiveTabData()
            });
          }
        })
        .catch(function(error) {
          scope.isLoading = false;
          scope.$buefy.dialog.alert({
            message: `A problem occurred with delete action. If the problem persists, please contact the IT Support at NewItem.IT.Support@thermofisher.com`,
            confirmText: "OK",
            type: "is-danger",
            onConfirm: () => (scope.uploadByFilterFile = null)
          });
        });
    },
    deleteAttachmentBySelectedItems(activeTab) {
      var scope = this;
      if (activeTab === 2) {
        this.$buefy.dialog.confirm({
          message: `Ce Certificate file will be deleted for selected items only. A maximum of 100 items per page are allowed.`,
          confirmText: "OK",
          type: "is-info",
          onConfirm: () => scope.deleteAttachmentFileBySelectedItems(activeTab)
        });
      } else if (activeTab === 3) {
        this.$buefy.dialog.confirm({
          message: `SDS Attach file will be deleted for selected items only. A maximum of 100 items per page are allowed.`,
          confirmText: "OK",
          type: "is-info",
          onConfirm: () => scope.deleteAttachmentFileBySelectedItems(activeTab)
        });
      }
    },
    deleteAttachmentFileBySelectedItems(activeTab) {
      var scope = this;
      var attachmentName = "";
      var args = {};
      args.headers = {
        "content-type": "application/json"
      };

      scope.isLoading = true;

      if (activeTab === 2) {
        attachmentName = "ceCertificate";
      } else if (activeTab === 3) {
        attachmentName = "sdsAttach";
      }

      scope.$http
        .put(
          `${process.env.VUE_APP_API_BASE_DOMAIN}eu/deleteAttachmentsSelectedItems?attachmentName=${attachmentName}`,
          activeTab === 2
            ? scope.riptSelectRowIds
            : activeTab === 3
            ? scope.ricbSelectRowIds
            : "",
          args
        )
        // eslint-disable-next-line no-unused-vars
        .then(function(response) {
          scope.isLoading = false;
          scope.$buefy.dialog.alert({
            message: `Delete File action successful.`,
            confirmText: "OK",
            type: "is-success",
            onConfirm: () => (
              scope.loadActiveTabData(), (scope.resetSelection = true)
            )
          });
        })
        .catch(function(error) {
          scope.isLoading = false;
          scope.$buefy.dialog.alert({
            message: `A problem occured with delete action. Please clear the cache and try again. If the problems persists, please contact the New Item Support at NewItem.IT.Support@thermofisher.com`,
            confirmText: "OK",
            type: "is-danger"
          });
        });
    },
    deleteAttachmentToTotalSubmission(activeTab) {
      var scope = this;
      if (activeTab === 2) {
        this.$buefy.dialog.confirm({
          message: `Ce Certificate file will be deleted for all items across the submission.`,
          confirmText: "OK",
          type: "is-info",
          onConfirm: () =>
            scope.deleteAttachmentFileToTotalSubmission(activeTab)
        });
      } else if (activeTab === 3) {
        this.$buefy.dialog.confirm({
          message: `SDS Attach file will be deleted for all items across the submission.`,
          confirmText: "OK",
          type: "is-info",
          onConfirm: () =>
            scope.deleteAttachmentFileToTotalSubmission(activeTab)
        });
      }
    },
    deleteAttachmentFileToTotalSubmission(activeTab) {
      var scope = this;
      var attachmentName = "";
      scope.isLoading = true;

      if (activeTab === 2) {
        attachmentName = "ceCertificate";
      } else if (activeTab === 3) {
        attachmentName = "sdsAttach";
      }

      scope
        .$http({
          url: `${process.env.VUE_APP_API_BASE_DOMAIN}eu/bulkDeleteAttachments?nbrNewItemCrKey=${scope.euSubmissionID}&attachmentName=${attachmentName}`,
          method: "delete"
        })
        // eslint-disable-next-line no-unused-vars
        .then(function(response) {
          scope.isLoading = false;
          if (response.data.toLowerCase() === "background") {
            scope.$buefy.dialog.alert({
              message: `File is being processed for upload in the background. You will be notified via email once it is complete.`,
              confirmText: "OK",
              type: "is-success",
              onConfirm: () => window.close()
            });
          } else if (response.data.toLowerCase() === "failure") {
            scope.$buefy.dialog.alert({
              message: `A problem occurred with the upload file used. Please remove any special characters or extra line breaks on the description fields and try again. If the problem persists, please contact the IT Support at NewItem.IT.Support@thermofisher.com`,
              confirmText: "OK",
              type: "is-danger"
            });
          } else {
            scope.$buefy.dialog.alert({
              message: `Delete File action successful.`,
              confirmText: "OK",
              type: "is-success",
              onConfirm: () => scope.loadActiveTabData()
            });
          }
        })
        .catch(function(error) {
          scope.isLoading = false;
          scope.$buefy.dialog.alert({
            message: `A problem occured with delete action. Please clear the cache and try again. If the problems persists, please contact the New Item Support at NewItem.IT.Support@thermofisher.com`,
            confirmText: "OK",
            type: "is-danger"
          });
        });
    },
    deleteSubmissionItems() {
      this.$root.$emit("rowIdsForDeleteAction", true);
    },
    deleteSubmissionAttachment() {
      var scope = this;
      scope.isLoading = true;
      scope
        .$http({
          url: `${process.env.VUE_APP_API_BASE_DOMAIN}eu/deleteSubAttachment?nbrNewItemCrKey=${scope.euSubmissionID}`,
          method: "delete"
        })
        // eslint-disable-next-line no-unused-vars
        .then(function(response) {
          scope.isLoading = false;
          scope.$buefy.dialog.alert({
            message: `Delete File action successful.`,
            confirmText: "OK",
            type: "is-success",
            onConfirm: () => scope.loadSubmissionDetails()
          });
        })
        .catch(function(error) {
          scope.isLoading = false;
          scope.$buefy.dialog.alert({
            message: `A problem occured with delete action. Please clear the cache and try again. If the problems persists, please contact the New Item Support at NewItem.IT.Support@thermofisher.com`,
            confirmText: "OK",
            type: "is-danger"
          });
        });
    },
    downloadSubmissionAttachment() {
      var scope = this;
      scope.isLoading = true;
      scope
        .$http({
          url: `${process.env.VUE_APP_API_BASE_DOMAIN}eu/downloadSubAttachment?nbrNewItemCrKey=${scope.euSubmissionID}`,
          method: "GET",
          responseType: "blob"
        })
        .then(function(response) {
          scope.isLoading = false;
          var url = window.URL.createObjectURL(new Blob([response.data]));
          var link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", scope.submissionAttachmentName);
          document.body.appendChild(link);
          link.click();
        })
        .catch(function(error) {
          scope.isLoading = false;
          scope.$buefy.dialog.alert({
            message: `A problem occurred with File download. Please clear the cache and try again. If the problem persists, please contact the IT Support at NewItem.IT.Support@thermofisher.com`,
            confirmText: "OK",
            type: "is-danger"
          });
        });
    },
    downloadSubmissionData(status) {
      var scope = this;
      var fileName = `Submission_${scope.euSubmissionID}.xlsx`;
      scope.isLoading = true;
      scope
        .$http({
          url: `${
            process.env.VUE_APP_API_BASE_DOMAIN
          }eu/downloadSubmission?nbrNewItemCrKey=${scope.euSubmissionID}${
            status ? "&itemStatus=" + status : ""
          }`,
          method: "GET",
          responseType: "blob"
        })
        .then(function(response) {
          scope.isLoading = false;
          var url = window.URL.createObjectURL(new Blob([response.data]));
          var link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        // eslint-disable-next-line no-unused-vars
        .catch(function(error) {
          scope.isLoading = false;
          scope.$buefy.dialog.alert({
            message: `A problem occurred with file download. Please clear the cache and try again. If the problem persists, please contact the IT Support at NewItem.IT.Support@thermofisher.com`,
            confirmText: "OK",
            type: "is-danger"
          });
        });
    },
    downloadTabData(tab) {
      var scope = this;
      var tabName = "";
      var fileName = "";
      switch (tab) {
        case "GI":
          tabName = "General Info";
          fileName = `Submission_${scope.euSubmissionID}_General_Information.xlsx`;
          break;
        case "PI":
          tabName = "Price Info";
          fileName = `Submission_${scope.euSubmissionID}_Price_Information.xlsx`;
          break;
        case "RIPT":
          tabName = "Reg product Info";
          fileName = `Submission_${scope.euSubmissionID}_Regulatory_Information_Product_Types.xlsx`;
          break;
        case "RICB":
          tabName = "Reg Chemical Info";
          fileName = `Submission_${scope.euSubmissionID}_Regulatory_Information_Chemical_Biological.xlsx`;
          break;
        case "RIEI":
          tabName = "Reg Equipment Info";
          fileName = `Submission_${scope.euSubmissionID}_Regulatory_Information_Equipment_Instrument.xlsx`;
          break;
      }

      scope.isLoading = true;
      scope
        .$http({
          url: `${process.env.VUE_APP_API_BASE_DOMAIN}eu/downloadSubmissionTabInfo?nbrNewItemCrKey=${scope.euSubmissionID}&tabName=${tabName}`,
          method: "GET",
          responseType: "blob"
        })
        .then(function(response) {
          scope.isLoading = false;
          var url = window.URL.createObjectURL(new Blob([response.data]));
          var link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        // eslint-disable-next-line no-unused-vars
        .catch(function(error) {
          scope.isLoading = false;
          scope.$buefy.dialog.alert({
            message: `A problem occurred with file download. Please clear the cache and try again. If the problem persists, please contact the IT Support at NewItem.IT.Support@thermofisher.com`,
            confirmText: "OK",
            type: "is-danger"
          });
        });
    },
    loadActiveTabData() {
      switch (this.activeTab) {
        case 0:
          this.loadEUGeneralInfoTab();
          break;
        case 1:
          this.loadEUPriceInfoTab();
          break;
        case 2:
          this.loadEURegulatoryInfoProductTypesTab();
          break;
        case 3:
          this.loadEURegulatoryInfoChemicalBiologicalTab();
          break;
        case 4:
          this.loadEURegulatoryInfoEquipmentInstrumentTab();
          break;
      }
    },
    selectIdsForRIPTUpload(value) {
      this.riptSelectRowIds = value;
    },
    selectIdsForRICBUpload(value) {
      this.ricbSelectRowIds = value;
    },
    updateGIErrorCount(count) {
      this.giErrorCountOnTabOpen = count;
    },
    updatePIErrorCount(count) {
      this.piErrorCountOnTabOpen = count;
    },
    updateRIPTErrorCount(count) {
      this.riptErrorCountOnTabOpen = count;
    },
    updateRICBErrorCount(count) {
      this.ricbErrorCountOnTabOpen = count;
    },
    updateRIEIErrorCount(count) {
      this.rieiErrorCountOnTabOpen = count;
    },
    uploadSubmissionData() {
      var scope = this;
      var isUploadFormatValid = /^.*\.(xls|xlsx)$/.test(
        scope.submissionUploadFile.name
      );
      if (isUploadFormatValid) {
        var args = {
          headers: {
            "content-type": "multipart/form-data"
          }
        };
        var userData = JSON.parse(sessionStorage.getItem("se_ni_user_eu"));
        var uploadData = new FormData();
        uploadData.append("file", scope.submissionUploadFile);

        scope.isLoading = true;
        scope.$http
          .post(
            `${process.env.VUE_APP_API_BASE_DOMAIN}eu/excel/uploadSubmissionData?nbrNewItemCrKey=${scope.euSubmissionID}&networkId=${userData.networkId}`,
            uploadData,
            args
          )
          .then(function(response) {
            scope.isLoading = false;
            scope.submissionUploadFile = null;
            if (response.data.toLowerCase() === "background") {
              scope.$buefy.dialog.alert({
                message: `File is being processed for upload in the background. You will be notified via email once it is complete.`,
                confirmText: "OK",
                type: "is-success",
                onConfirm: () => window.close()
              });
            } else if (response.data.toLowerCase() === "failure") {
              scope.$buefy.dialog.alert({
                message: `A problem occurred with the upload file used. Please remove any special characters or extra line breaks on the description fields and try again. If the problem persists, please contact the IT Support at NewItem.IT.Support@thermofisher.com`,
                confirmText: "OK",
                type: "is-danger"
              });
            } else {
              scope.$buefy.dialog.alert({
                message: `Upload successful for Submission - ${scope.euSubmissionID}.`,
                confirmText: "OK",
                type: "is-success",
                onConfirm: () => (
                  (window.uploadComplete = true),
                  //reload based on tab index
                  scope.loadActiveTabData()
                )
              });
            }
          })
          // eslint-disable-next-line no-unused-vars
          .catch(function(error) {
            scope.isLoading = false;
            if (error.response.status === 400) {
              scope.$buefy.dialog.alert({
                message: error.response.data,
                confirmText: "OK",
                type: "is-danger"
              });
            } else {
              scope.$buefy.dialog.alert({
                message: `A problem occurred with the upload file used. Please remove any special characters or extra line breaks on the description fields and try again. If the problem persists, please contact the IT Support at NewItem.IT.Support@thermofisher.com`,
                confirmText: "OK",
                type: "is-danger"
              });
            }
          });
      } else {
        scope.$buefy.dialog.alert({
          message: `"${scope.submissionUploadFile.name}" - Invalid file type, system accepts only .xls(x) extension.`,
          confirmText: "OK",
          type: "is-danger"
        });
      }
    },
    uploadTabData(tab) {
      var scope = this;
      var tabName = "";
      switch (tab) {
        case "GI":
          tabName = "General Info";
          break;
        case "PI":
          tabName = "Price Info";
          break;
        case "RIPT":
          tabName = "Reg product Info";
          break;
        case "RICB":
          tabName = "Reg Chemical Info";
          break;
        case "RIEI":
          tabName = "Reg Equipment Info";
          break;
      }

      var isUploadFormatValid = /^.*\.(xls|xlsx)$/.test(
        scope.tabUploadFile.name
      );
      if (isUploadFormatValid) {
        var args = {
          headers: {
            "content-type": "multipart/form-data"
          }
        };
        var userData = JSON.parse(sessionStorage.getItem("se_ni_user_eu"));
        var uploadData = new FormData();
        uploadData.append("file", scope.tabUploadFile);

        scope.isLoading = true;
        scope.$http
          .post(
            `${process.env.VUE_APP_API_BASE_DOMAIN}eu/excel/uploadTabLevelData?nbrNewItemCrKey=${scope.euSubmissionID}&networkId=${userData.networkId}&tabName=${tabName}`,
            uploadData,
            args
          )
          .then(function(response) {
            scope.isLoading = false;
            scope.tabUploadFile = null;
            if (response.data.toLowerCase() === "background") {
              scope.$buefy.dialog.alert({
                message: `File is being processed for upload in the background. You will be notified via email once it is complete.`,
                confirmText: "OK",
                type: "is-success",
                onConfirm: () => window.close()
              });
            } else if (response.data.toLowerCase() === "failure") {
              scope.$buefy.dialog.alert({
                message: `A problem occurred with the upload file used. Please remove any special characters or extra line breaks on the description fields and try again. If the problem persists, please contact the IT Support at NewItem.IT.Support@thermofisher.com`,
                confirmText: "OK",
                type: "is-danger"
              });
            } else {
              scope.$buefy.dialog.alert({
                message: `Upload successful for Submission - ${scope.euSubmissionID}.`,
                confirmText: "OK",
                type: "is-success",
                onConfirm: () => (
                  (window.uploadComplete = true),
                  //reload active tab data
                  scope.loadActiveTabData()
                )
              });
            }
          })
          // eslint-disable-next-line no-unused-vars
          .catch(function(error) {
            scope.isLoading = false;
            if (error.response.status === 400) {
              scope.$buefy.dialog.alert({
                message: error.response.data,
                confirmText: "O",
                type: "is-danger"
              });
            } else {
              scope.$buefy.dialog.alert({
                message: `A problem occurred with the upload file used. Please remove any special characters or extra line breaks on the description fields and try again. If the problem persists, please contact the IT Support at NewItem.IT.Support@thermofisher.com`,
                confirmText: "OK",
                type: "is-danger"
              });
            }
          });
      } else {
        scope.$buefy.dialog.alert({
          message: `"${scope.tabUploadFile.name}" - Invalid file type, system accepts only .xls(x) extension.`,
          confirmText: "OK",
          type: "is-danger"
        });
      }
    },
    loadEUGeneralInfoTab() {
      this.canLoadGIData = {
        loadDetails: true,
        actions: {
          fErrors: this.gifilterMapErrors,
          fSuppItemNbr: this.gifilterMapSuppItemNbr,
          fProdCtgry: this.gifilterMapProdCtgry,
          fProductNoun: this.gifilterMapProductNoun,
          fReworkItem: this.gifilterMapReworkItems,
          sortSuppItemNbr: this.giSortMapSuppItemNbrOrder,
          srFieldName: this.gisearchMapFieldName,
          srFieldValue: this.gisearchMapInput
        },
        paginationParams: {
          pageIndex: this.giPageIndex,
          numberOfItems: this.giNumberOfItems
        }
      };
    },
    loadEUPriceInfoTab() {
      this.canLoadPIData = {
        loadDetails: true,
        // generalQuestionRows: [],
        actions: {
          fErrors: this.pifilterMapErrors,
          fSuppItemNbr: this.pifilterMapSuppItemNbr,
          fProductNoun: this.pifilterMapProductNoun,
          fReworkItem: this.pifilterMapReworkItems,
          sortSuppItemNbr: this.pisortMapSuppItemNbrOrder,
          srFieldName: this.pisearchMapFieldName,
          srFieldValue: this.pisearchMapInput
        },
        paginationParams: {
          pageIndex: this.piPageIndex,
          numberOfItems: this.piNumberOfItems
        }
      };
    },
    loadEURegulatoryInfoProductTypesTab() {
      this.canLoadRIPTData = {
        loadDetails: true,
        // generalQuestionRows: [],
        actions: {
          fErrors: this.riptfilterMapErrors,
          fSuppItemNbr: this.riptfilterMapSuppItemNbr,
          fProductNoun: this.riptfilterMapProductNoun,
          fCeMarked: this.riptfilterMapItemCeMarked,
          fReworkItem: this.riptfilterMapReworkItems,
          sortSuppItemNbr: this.riptsortMapSuppItemNbrOrder,
          srFieldName: this.riptsearchMapFieldName,
          srFieldValue: this.riptsearchMapInput
        },
        paginationParams: {
          pageIndex: this.riPtPageIndex,
          numberOfItems: this.riPtNumberOfItems
        }
      };
    },
    loadEURegulatoryInfoChemicalBiologicalTab() {
      this.canLoadRICBData = {
        loadDetails: true,
        // generalQuestionRows: [],
        actions: {
          fErrors: this.ricbfilterMapErrors,
          fSuppItemNbr: this.ricbfilterMapSuppItemNbr,
          fProductNoun: this.ricbfilterMapProductNoun,
          fDangGood: this.ricbfilterMapDangGood,
          fReworkItem: this.ricbfilterMapReworkItems,
          sortSuppItemNbr: this.ricbsortMapSuppItemNbrOrder,
          srFieldName: this.ricbsearchMapFieldName,
          srFieldValue: this.ricbsearchMapInput
        },
        paginationParams: {
          pageIndex: this.riCbPageIndex,
          numberOfItems: this.riCbNumberOfItems
        }
      };
    },
    loadEURegulatoryInfoEquipmentInstrumentTab() {
      this.canLoadRIEIData = {
        loadDetails: true,
        // generalQuestionRows: [],
        actions: {
          fErrors: this.rieifilterMapErrors,
          fSuppItemNbr: this.rieifilterMapSuppItemNbr,
          fProductNoun: this.rieifilterMapProductNoun,
          fReworkItem: this.rieifilterMapReworkItems,
          sortSuppItemNbr: this.rieisortMapSuppItemNbrOrder,
          srFieldName: this.rieisearchMapFieldName,
          srFieldValue: this.rieisearchMapInput
        },
        paginationParams: {
          pageIndex: this.riEiPageIndex,
          numberOfItems: this.riEiNumberOfItems
        }
      };
    },
    loadItemDetails() {},
    loadSubmissionDetails() {
      var scope = this;

      scope.isLoading = true;
      scope.$http
        .get(
          `${process.env.VUE_APP_API_BASE_DOMAIN}eu/getSupplierInfo/submissionId/${scope.euSubmissionID}`
        )
        .then(function(response) {
          scope.isLoading = false;
          var data = response.data;
          //load form info
          scope.suppNbr = data.suppId ? data.suppId : "";
          scope.suppName = data.suppName ? data.suppName : "";
          scope.website = data.website ? data.website : "";
          scope.submissionDesc = data.subDesc ? data.subDesc : "";
          scope.createdDate = data.dateCreated
            ? data.dateCreated.replace("T", " ").split(".")[0]
            : "";
          scope.createdBy = data.createdBy ? data.createdBy : "";
          scope.prodContactName = data.prodCntctName ? data.prodCntctName : "";
          scope.prodContactEmail = data.prodCntctEmail
            ? data.prodCntctEmail
            : "";
          scope.prodCount = data.prodCount;
          scope.launchDate = data.launchDate ? new Date(data.launchDate) : null;
          scope.regContactName = data.regCntctName ? data.regCntctName : "";
          scope.regContactPhone = data.regCntctPhone ? data.regCntctPhone : "";
          scope.regContactEmail = data.regCntctEmail ? data.regCntctEmail : "";
          if (data.attachmentName) {
            scope.submissionAttachmentName = data.attachmentName;
          } else {
            scope.submissionAttachmentName = "";
            scope.submissionAttachment = null;
          }
          scope.submissionStatus = data.status;
          //check validate button value
          scope.disableSuppValidateBtn = data.validateForm === "Y";
          scope.validateSubmissionLevel();
          scope.$nextTick(function() {
            var errorObj = {};
            errorObj.totalErrorCount = data.errorCount;
            scope.validateSupplierSubmit(errorObj);
          });
          //tabs error count
          scope.subGiTabErrorCount = data.giTabErrorCount;
          scope.subPiTabErrorCount = data.piTabErrorCount;
          scope.subRiProdTabErrorCount = data.riProdTabErrorCount;
          scope.subRiChemTabErrorCount = data.riChemTabErrorCount;
          scope.subRiEquipTabErrorCount = data.riEquipTabErrorCount;
          sessionStorage.removeItem("editRequestFromSeEu");
          sessionStorage.removeItem("viewRequestFromSeEu");
          sessionStorage.removeItem("reViewRequestFromSeEu");
        })
        .catch(function(error) {
          scope.isLoading = false;
          sessionStorage.removeItem("editRequestFromSeEu");
          sessionStorage.removeItem("viewRequestFromSeEu");
          sessionStorage.removeItem("reViewRequestFromSeEu");
          scope.$buefy.dialog.alert({
            message: `Failed to load submission details. Please clear the cache and try again. If the problem persists, please contact the IT Support at NewItem.IT.Support@thermofisher.com`,
            confirmText: "OK",
            type: "is-danger"
          });
        });
    },
    resetFilterSortSearchInputs() {
      switch (this.activeTab) {
        case 0:
          this.gifilterMapErrors = "";
          this.gifilterMapSuppItemNbr = "";
          this.gifilterMapProductNoun = "";
          this.gifilterMapProdCtgry = "";
          this.gifilterMapReworkItems = false;
          this.giSortMapSuppItemNbrOrder = "";
          this.gisearchMapFieldName = "";
          this.gisearchMapInput = "";
          break;
        case 1:
          this.pifilterMapErrors = "";
          this.pifilterMapSuppItemNbr = "";
          this.pifilterMapProductNoun = "";
          this.pifilterMapReworkItems = false;
          this.pisortMapSuppItemNbrOrder = "";
          this.pisearchMapFieldName = "";
          this.pisearchMapInput = "";
          break;
        case 2:
          this.riptfilterMapErrors = "";
          this.riptfilterMapSuppItemNbr = "";
          this.riptfilterMapProductNoun = "";
          this.riptfilterMapItemCeMarked = "";
          this.riptfilterMapReworkItems = false;
          this.riptsortMapSuppItemNbrOrder = "";
          this.riptsearchMapFieldName = "";
          this.riptsearchMapInput = "";
          break;
        case 3:
          this.ricbfilterMapErrors = "";
          this.ricbfilterMapSuppItemNbr = "";
          this.ricbfilterMapProductNoun = "";
          this.ricbfilterMapDangGood = "";
          this.ricbfilterMapReworkItems = false;
          this.ricbsortMapSuppItemNbrOrder = "";
          this.ricbsearchMapFieldName = "";
          this.ricbsearchMapInput = "";
          break;
        case 4:
          this.rieifilterMapErrors = "";
          this.rieifilterMapSuppItemNbr = "";
          this.rieifilterMapProductNoun = "";
          this.rieifilterMapReworkItems = false;
          this.rieisortMapSuppItemNbrOrder = "";
          this.rieisearchMapFieldName = "";
          this.rieisearchMapInput = "";
          break;
      }
      this.loadActiveTabData();
    },
    showErrorsByTabIndex() {
      this.errorsModalProps.submissionId = this.euSubmissionID;
      this.isViewUploadErrorsModalActive = true;
      switch (this.activeTab) {
        case 0:
          this.errorsModalProps.tab = "GI";
          break;
        case 1:
          this.errorsModalProps.tab = "PI";
          break;
        case 2:
          this.errorsModalProps.tab = "RIPT";
          break;
        case 3:
          this.errorsModalProps.tab = "RICB";
          break;
        case 4:
          this.errorsModalProps.tab = "RIEI";
          break;
      }
    },
    saveEuSubmission() {
      var scope = this;
      var args = {};
      args.headers = {
        "content-type": "application/json"
      };
      var requestPayload = {};
      requestPayload.suppId = scope.suppNbr;
      requestPayload.suppName = scope.suppName;
      requestPayload.website = scope.website;
      requestPayload.subDesc = scope.submissionDesc;
      requestPayload.createdDate = scope.createdDate;
      requestPayload.createdBy = scope.createdBy;
      requestPayload.prodCntctName = scope.prodContactName;
      requestPayload.prodCntctEmail = scope.prodContactEmail;
      requestPayload.launchDate = scope.launchDate;
      requestPayload.regCntctName = scope.regContactName;
      requestPayload.regCntctPhone = scope.regContactPhone;
      requestPayload.regCntctEmail = scope.regContactEmail;

      scope.isLoading = true;
      scope.$http
        .put(
          `${process.env.VUE_APP_API_BASE_DOMAIN}eu/updateSupplierInfo?nbrNewItemCrKey=${scope.euSubmissionID}`,
          requestPayload,
          args
        )
        .then(function(response) {
          scope.isLoading = false;
          var data = response.data;
          //load form info
          scope.suppNbr = data.suppId ? data.suppId : "";
          scope.suppName = data.suppName ? data.suppName : "";
          scope.website = data.website ? data.website : "";
          scope.submissionDesc = data.subDesc ? data.subDesc : "";
          scope.createdDate = data.dateCreated
            ? data.dateCreated.replace("T", " ").split(".")[0]
            : "";
          scope.createdBy = data.createdBy ? data.createdBy : "";
          scope.prodContactName = data.prodCntctName ? data.prodCntctName : "";
          scope.prodContactEmail = data.prodCntctEmail
            ? data.prodCntctEmail
            : "";
          if (data.launchDate) {
            scope.launchDate = new Date(data.launchDate);
          }
          scope.regContactName = data.regCntctName ? data.regCntctName : "";
          scope.regContactPhone = data.regCntctPhone ? data.regCntctPhone : "";
          scope.regContactEmail = data.regCntctEmail ? data.regCntctEmail : "";
          //get supplier attachment
          scope.submissionAttachmentName = data.attachmentName;
          //check validate button value
          scope.disableSuppValidateBtn = data.validateForm === "Y";
          scope.validateSubmissionLevel();
          scope.$nextTick(function() {
            var errorObj = {};
            errorObj.totalErrorCount = data.errorCount;
            scope.validateSupplierSubmit(errorObj);
          });
          sessionStorage.removeItem("editRequestFromSeEu");
          sessionStorage.removeItem("viewRequestFromSeEu");
          sessionStorage.removeItem("reViewRequestFromSeEu");
        })
        .catch(function(error) {
          scope.isLoading = false;
          sessionStorage.removeItem("editRequestFromSeEu");
          sessionStorage.removeItem("viewRequestFromSeEu");
          sessionStorage.removeItem("reViewRequestFromSeEu");
          scope.$buefy.dialog.alert({
            message: `Failed to load submission details. Please clear the cache and try again. If the problem persists, please contact the IT Support at NewItem.IT.Support@thermofisher.com`,
            confirmText: "OK",
            type: "is-danger"
          });
        });
    },
    submitCancelWorkflow(statusCode) {
      var scope = this;
      var userData = JSON.parse(sessionStorage.getItem("se_ni_user_eu"));

      scope.isLoading = true;
      scope.$http
        .get(
          `${process.env.VUE_APP_API_BASE_DOMAIN}eu/submitCancelWorkflow?nbrNewItemCrKey=${scope.euSubmissionID}&networkId=${userData.networkId}&status=${statusCode}`
        )
        // eslint-disable-next-line no-unused-vars
        .then(function(response) {
          scope.isLoading = false;
          scope.$buefy.dialog.alert({
            message: `Action on Submission ID - ${scope.euSubmissionID} is successful.`,
            confirmText: "OK",
            type: "is-success",
            onConfirm: () => scope.closeFormAfterSubmit()
          });
          //scope.$emit("toggleSubmissionView", true);
        })
        // eslint-disable-next-line no-unused-vars
        .catch(function(error) {
          scope.isLoading = false;
          if (error.response.data.noteMsg && error.response.data.errorMsg) {
            scope.$buefy.dialog.alert({
              title: `${error.response.data.noteMsg}`,
              message: `${error.response.data.errorMsg}`,
              confirmText: "OK",
              type: "is-danger"
            });
          } else if (
            !error.response.data.noteMsg &&
            error.response.data.errorMsg
          ) {
            scope.$buefy.dialog.alert({
              message: `${error.response.data.errorMsg}`,
              confirmText: "OK",
              type: "is-danger"
            });
          } else {
            scope.$buefy.dialog.alert({
              message: `Failed to update action on Submission ID - ${scope.euSubmissionID}.`,
              confirmText: "OK",
              type: "is-danger"
            });
          }
        });
    },
    supplierValidate() {
      var scope = this;
      scope.isLoading = true;

      scope
        .$http({
          url: `${process.env.VUE_APP_API_BASE_DOMAIN}eu/genlQstn/validate?nbrNewItemCrKey=${scope.euSubmissionID}&pageIndex=${scope.giPageIndex}&noOfRecords=${scope.giNumberOfItems}`,
          method: "get"
        })
        // eslint-disable-next-line no-unused-vars
        .then(function(response) {
          scope.isLoading = false;
          //load tab data based on activeTab index
          scope.loadSubmissionDetails();
          switch (scope.activeTab) {
            case 0:
              scope.loadEUGeneralInfoTab();
              break;
            case 1:
              scope.loadEUPriceInfoTab();
              break;
            case 2:
              scope.loadEURegulatoryInfoProductTypesTab();
              break;
            case 3:
              scope.loadEURegulatoryInfoChemicalBiologicalTab();
              break;
            case 4:
              scope.loadEURegulatoryInfoEquipmentInstrumentTab();
              break;
          }
          scope.validateTabHeaders();
        })
        .catch(function(error) {
          scope.isLoading = false;
          scope.$buefy.dialog.alert({
            message: `A problem occured with validate action. Please clear the cache and try again. If the problems persists, please contact the New Item Support at NewItem.IT.Support@thermofisher.com`,
            confirmText: "OK",
            type: "is-danger"
          });
        });
    },
    updateGIPaginationWrapper(params) {
      params.startIndex
        ? (this.giMinPageIndex = params.startIndex)
        : (this.giMinPageIndex = 0);
      params.endIndex !== undefined
        ? (this.giMaxPageIndex = params.endIndex)
        : (this.giMaxPageIndex = 0);
      params.totalCount !== undefined
        ? (this.giTotalResults = params.totalCount)
        : (this.giTotalResults = 0);
      params.totalPages !== undefined
        ? (this.giTotalPages = params.totalPages)
        : (this.giTotalPages = 0);
      if (params.startIndex <= 1) {
        this.giShowPreviousButton = false;
        this.giShowFirstButton = false;
      } else {
        this.giShowPreviousButton = true;
        this.giShowFirstButton = true;
      }
      if (this.giPageIndex >= this.giTotalPages) {
        this.giShowNextButton = false;
        this.giShowLastButton = false;
      } else {
        this.giShowNextButton = true;
        this.giShowLastButton = true;
      }
    },
    updatePIPaginationWrapper(params) {
      params.startIndex
        ? (this.piMinPageIndex = params.startIndex)
        : (this.piMinPageIndex = 0);
      params.endIndex !== undefined
        ? (this.piMaxPageIndex = params.endIndex)
        : (this.piMaxPageIndex = 0);
      params.totalCount !== undefined
        ? (this.piTotalResults = params.totalCount)
        : (this.piTotalResults = 0);
      params.totalPages !== undefined
        ? (this.piTotalPages = params.totalPages)
        : (this.piTotalPages = 0);
      if (params.startIndex <= 1) {
        this.piShowPreviousButton = false;
        this.piShowFirstButton = false;
      } else {
        this.piShowPreviousButton = true;
        this.piShowFirstButton = true;
      }
      if (this.piPageIndex >= this.piTotalPages) {
        this.piShowNextButton = false;
        this.piShowLastButton = false;
      } else {
        this.piShowNextButton = true;
        this.piShowLastButton = true;
      }
    },
    updateRIPTPaginationWrapper(params) {
      params.startIndex
        ? (this.riPtMinPageIndex = params.startIndex)
        : (this.riPtMinPageIndex = 0);
      params.endIndex !== undefined
        ? (this.riPtMaxPageIndex = params.endIndex)
        : (this.riPtMaxPageIndex = 0);
      params.totalCount !== undefined
        ? (this.riPtTotalResults = params.totalCount)
        : (this.riPtTotalResults = 0);
      params.totalPages !== undefined
        ? (this.riPtTotalPages = params.totalPages)
        : (this.riPtTotalPages = 0);
      if (params.startIndex <= 1) {
        this.riPtShowPreviousButton = false;
        this.riPtShowFirstButton = false;
      } else {
        this.riPtShowPreviousButton = true;
        this.riPtShowFirstButton = true;
      }
      if (this.riPtPageIndex >= this.riPtTotalPages) {
        this.riPtShowNextButton = false;
        this.riPtShowLastButton = false;
      } else {
        this.riPtShowNextButton = true;
        this.riPtShowLastButton = true;
      }
    },
    updateRICBPaginationWrapper(params) {
      params.startIndex
        ? (this.riCbMinPageIndex = params.startIndex)
        : (this.riCbMinPageIndex = 0);
      params.endIndex !== undefined
        ? (this.riCbMaxPageIndex = params.endIndex)
        : (this.riCbMaxPageIndex = 0);
      params.totalCount !== undefined
        ? (this.riCbTotalResults = params.totalCount)
        : (this.riCbTotalResults = 0);
      params.totalPages !== undefined
        ? (this.riCbTotalPages = params.totalPages)
        : (this.riCbTotalPages = 0);
      if (params.startIndex <= 1) {
        this.riCbShowPreviousButton = false;
        this.riCbShowFirstButton = false;
      } else {
        this.riCbShowPreviousButton = true;
        this.riCbShowFirstButton = true;
      }
      if (this.riCbPageIndex >= this.riCbTotalPages) {
        this.riCbShowNextButton = false;
        this.riCbShowLastButton = false;
      } else {
        this.riCbShowNextButton = true;
        this.riCbShowLastButton = true;
      }
    },
    updateRIEIPaginationWrapper(params) {
      params.startIndex
        ? (this.riEiMinPageIndex = params.startIndex)
        : (this.riEiMinPageIndex = 0);
      params.endIndex !== undefined
        ? (this.riEiMaxPageIndex = params.endIndex)
        : (this.riEiMaxPageIndex = 0);
      params.totalCount !== undefined
        ? (this.riEiTotalResults = params.totalCount)
        : (this.riEiTotalResults = 0);
      params.totalPages !== undefined
        ? (this.riEiTotalPages = params.totalPages)
        : (this.riEiTotalPages = 0);
      if (params.startIndex <= 1) {
        this.riEiShowPreviousButton = false;
        this.riEiShowFirstButton = false;
      } else {
        this.riEiShowPreviousButton = true;
        this.riEiShowFirstButton = true;
      }
      if (this.riEiPageIndex >= this.riEiTotalPages) {
        this.riEiShowNextButton = false;
        this.riEiShowLastButton = false;
      } else {
        this.riEiShowNextButton = true;
        this.riEiShowLastButton = true;
      }
    },
    uploadAttachmentByFilter(activeTab) {
      var scope = this;
      if (activeTab === 2) {
        this.$buefy.dialog.confirm({
          message: `Ce Certificate file will be uploaded for all items across the submission based on filter criteria.`,
          confirmText: "OK",
          type: "is-info",
          onConfirm: () => scope.uploadAttachmentFileByFilter(activeTab)
        });
      } else if (activeTab === 3) {
        this.$buefy.dialog.confirm({
          message: `SDS Attach file will be uploaded for all items across the submission based on filter criteria.`,
          confirmText: "OK",
          type: "is-info",
          onConfirm: () => scope.uploadAttachmentFileByFilter(activeTab)
        });
      }
    },
    uploadAttachmentFileByFilter(activeTab) {
      var scope = this;
      var isUploadValid = /^.*\.(pdf|xls|xlsx)$/.test(
        this.uploadByFilterFile.name.toLowerCase()
      );
      if (isUploadValid) {
        var args = {
          headers: {
            "content-type": "form-data"
          }
        };

        //filterMap for upload
        var requestPayload = {};
        requestPayload.actions = [];
        requestPayload.filterMap = {};
        requestPayload.searchMap = {};
        requestPayload.sortMap = {};

        var uploadData = new FormData();
        if (this.activeTab === 2) {
          uploadData.append("ceCertificate", scope.uploadByFilterFile);
          //filter for ceCert
          if (
            this.canLoadRIPTData.actions.fErrors ||
            this.canLoadRIPTData.actions.fSuppItemNbr ||
            this.canLoadRIPTData.actions.fProductNoun ||
            this.canLoadRIPTData.actions.fCeMarked ||
            this.canLoadRIPTData.actions.fReworkItem
          ) {
            requestPayload.actions.push("Filter");
            requestPayload.filterMap = {};
            if (this.canLoadRIPTData.actions.fErrors) {
              requestPayload.filterMap.ErroredItems = this.canLoadRIPTData.actions.fErrors;
            }
            if (this.canLoadRIPTData.actions.fSuppItemNbr) {
              requestPayload.filterMap.suppItemNbr = this.canLoadRIPTData.actions.fSuppItemNbr;
            }
            if (this.canLoadRIPTData.actions.fProductNoun) {
              requestPayload.filterMap.englishPrdctNoun = this.canLoadRIPTData.actions.fProductNoun;
            }
            if (this.canLoadRIPTData.actions.fCeMarked) {
              requestPayload.filterMap.itemCeMarked = this.canLoadRIPTData.actions.fCeMarked;
            }
            if (this.canLoadRIPTData.actions.fReworkItem) {
              requestPayload.filterMap.itemStatus = "K";
            }
          }
          if (this.canLoadRICBData.actions.sortSuppItemNbr) {
            requestPayload.actions.push("Sort");
            requestPayload.sortMap = {};
            requestPayload.sortMap.suppItemNbr = this.canLoadRIPTData.actions.sortSuppItemNbr;
          }
          if (
            this.canLoadRICBData.actions.srFieldName &&
            this.canLoadRIPTData.actions.srFieldValue
          ) {
            requestPayload.actions.push("Search");
            requestPayload.searchMap = {};
            requestPayload.searchMap[
              this.canLoadRIPTData.actions.srFieldName
            ] = this.canLoadRIPTData.actions.srFieldValue;
          }
        } else if (this.activeTab === 3) {
          uploadData.append("sdsAttach", scope.uploadByFilterFile);
          //filter for sdsAttach
          if (
            this.canLoadRICBData.actions.fErrors ||
            this.canLoadRICBData.actions.fSuppItemNbr ||
            this.canLoadRICBData.actions.fProductNoun ||
            this.canLoadRICBData.actions.fDangGood ||
            this.canLoadRICBData.actions.fReworkItem
          ) {
            requestPayload.actions.push("Filter");
            requestPayload.filterMap = {};
            if (this.canLoadRICBData.actions.fErrors) {
              requestPayload.filterMap.ErroredItems = this.canLoadRICBData.actions.fErrors;
            }
            if (this.canLoadRICBData.actions.fSuppItemNbr) {
              requestPayload.filterMap.suppItemNbr = this.canLoadRICBData.actions.fSuppItemNbr;
            }
            if (this.canLoadRICBData.actions.fProductNoun) {
              requestPayload.filterMap.englishPrdctNoun = this.canLoadRICBData.actions.fProductNoun;
            }
            if (this.canLoadRICBData.actions.fDangGood) {
              requestPayload.filterMap.dangGood = this.canLoadRICBData.actions.fDangGood;
            }
            if (this.canLoadRICBData.actions.fReworkItem) {
              requestPayload.filterMap.itemStatus = "K";
            }
          }
          if (this.canLoadRICBData.actions.sortSuppItemNbr) {
            requestPayload.actions.push("Sort");
            requestPayload.sortMap = {};
            requestPayload.sortMap.suppItemNbr = this.canLoadRICBData.actions.sortSuppItemNbr;
          }
          if (
            this.canLoadRICBData.actions.srFieldName &&
            this.canLoadRICBData.actions.srFieldValue
          ) {
            requestPayload.actions.push("Search");
            requestPayload.searchMap = {};
            requestPayload.searchMap[
              this.canLoadRICBData.actions.srFieldName
            ] = this.canLoadRICBData.actions.srFieldValue;
          }
        }
        uploadData.append("payload", JSON.stringify(requestPayload));

        scope.isLoading = true;
        scope.$http
          .post(
            `${process.env.VUE_APP_API_BASE_DOMAIN}eu/uploadAttachmentsFilterItems?nbrNewItemCrKey=${scope.euSubmissionID}`,
            uploadData,
            args
          )
          // eslint-disable-next-line no-unused-vars
          .then(function(response) {
            scope.isLoading = false;
            scope.uploadByFilterFile = null;
            if (response.data.toLowerCase() === "background") {
              scope.$buefy.dialog.alert({
                message: `File is being processed for upload in the background. You will be notified via email once it is complete.`,
                confirmText: "OK",
                type: "is-success",
                onConfirm: () => window.close()
              });
            } else if (response.data.toLowerCase() === "failure") {
              scope.$buefy.dialog.alert({
                message: `A problem occurred with the upload file used. Please remove any special characters or extra line breaks on the description fields and try again. If the problem persists, please contact the IT Support at NewItem.IT.Support@thermofisher.com`,
                confirmText: "OK",
                type: "is-danger"
              });
            } else {
              scope.$buefy.dialog.alert({
                message: `File upload successful.`,
                confirmText: "OK",
                type: "is-success",
                onConfirm: () => scope.loadActiveTabData()
              });
            }
          })
          .catch(function(error) {
            scope.isLoading = false;
            scope.$buefy.dialog.alert({
              message: `A problem occurred with the attachment used. Please ensure attachment has the one of the following extensions: pdf, png, jpg, eps, tiff and try again. If the problem persists, please contact the IT Support at NewItem.IT.Support@thermofisher.com`,
              confirmText: "OK",
              type: "is-danger",
              onConfirm: () => (scope.uploadByFilterFile = null)
            });
          });
      } else {
        scope.$buefy.dialog.alert({
          message: `"${scope.uploadByFilterFile.name}" - Invalid file type, system accepts only pdf, xls, xlsx format.`,
          confirmText: "OK",
          type: "is-danger",
          onConfirm: () => (scope.uploadByFilterFile = null)
        });
      }
    },
    uploadAttachmentBySelectedItems(activeTab) {
      var scope = this;
      if (activeTab === 2) {
        this.$buefy.dialog.confirm({
          message: `Ce Certificate file will be uploaded for selected items only. A maximum of 100 items per page are allowed.`,
          confirmText: "OK",
          type: "is-info",
          onConfirm: () => scope.uploadAttachmentFileBySelectedItems(activeTab)
        });
      } else if (activeTab === 3) {
        this.$buefy.dialog.confirm({
          message: `SDS Attach file will be uploaded for selected items only. A maximum of 100 items per page are allowed.`,
          confirmText: "OK",
          type: "is-info",
          onConfirm: () => scope.uploadAttachmentFileBySelectedItems(activeTab)
        });
      }
    },
    uploadAttachmentFileBySelectedItems(activeTab) {
      var scope = this;
      var isUploadValid = /^.*\.(pdf|xls|xlsx)$/.test(
        this.uploadBySelectedItemsFile.name.toLowerCase()
      );
      if (isUploadValid) {
        var args = {
          headers: {
            "content-type": "form-data"
          }
        };

        var uploadData = new FormData();
        if (activeTab === 2) {
          uploadData.append("ceCertificate", scope.uploadBySelectedItemsFile);
          uploadData.append(
            "genlInfoIds",
            JSON.stringify(scope.riptSelectRowIds)
          );
        } else if (activeTab === 3) {
          uploadData.append("sdsAttach", scope.uploadBySelectedItemsFile);
          uploadData.append(
            "genlInfoIds",
            JSON.stringify(scope.ricbSelectRowIds)
          );
        }

        scope.isLoading = true;
        scope.$http
          .post(
            `${process.env.VUE_APP_API_BASE_DOMAIN}eu/uploadAttachmentsSelectedItems`,
            uploadData,
            args
          )
          // eslint-disable-next-line no-unused-vars
          .then(function(response) {
            scope.isLoading = false;
            scope.uploadToTotalSubmissionFile = null;
            scope.$buefy.dialog.alert({
              message: `File upload successful.`,
              confirmText: "OK",
              type: "is-success",
              onConfirm: () => (
                scope.loadActiveTabData(), (scope.resetSelection = true)
              )
            });
          })
          .catch(function(error) {
            scope.isLoading = false;
            scope.$buefy.dialog.alert({
              message: `A problem occurred with the attachment used. Please ensure attachment has the one of the following extensions: pdf, png, jpg, eps, tiff and try again. If the problem persists, please contact the IT Support at NewItem.IT.Support@thermofisher.com`,
              confirmText: "OK",
              type: "is-danger",
              onConfirm: () => (scope.uploadBySelectedItemsFile = null)
            });
          });
      } else {
        scope.$buefy.dialog.alert({
          message: `"${scope.uploadBySelectedItemsFile.name}" - Invalid file type, system accepts only pdf, xls, xlsx format.`,
          confirmText: "OK",
          type: "is-danger",
          onConfirm: () => (scope.uploadBySelectedItemsFile = null)
        });
      }
    },
    uploadAttachmentToTotalSubmission(activeTab) {
      var scope = this;
      if (activeTab === 2) {
        this.$buefy.dialog.confirm({
          message: `Ce Certificate file will be uploaded for all items across the submission.`,
          confirmText: "OK",
          type: "is-info",
          onConfirm: () =>
            scope.uploadAttachmentFileToTotalSubmission(activeTab)
        });
      } else if (activeTab === 3) {
        this.$buefy.dialog.confirm({
          message: `SDS Attach file will be uploaded for all items across the submission.`,
          confirmText: "OK",
          type: "is-info",
          onConfirm: () =>
            scope.uploadAttachmentFileToTotalSubmission(activeTab)
        });
      }
    },
    uploadAttachmentFileToTotalSubmission(activeTab) {
      var scope = this;
      var isUploadValid = /^.*\.(pdf|xls|xlsx)$/.test(
        this.uploadToTotalSubmissionFile.name.toLowerCase()
      );
      if (isUploadValid) {
        var args = {
          headers: {
            "content-type": "form-data"
          }
        };

        var uploadData = new FormData();
        if (activeTab === 2) {
          uploadData.append("ceCertificate", scope.uploadToTotalSubmissionFile);
        } else if (activeTab === 3) {
          uploadData.append("sdsAttach", scope.uploadToTotalSubmissionFile);
        }

        scope.isLoading = true;
        scope.$http
          .post(
            `${process.env.VUE_APP_API_BASE_DOMAIN}eu/bulkUploadAttachments?nbrNewItemCrKey=${scope.euSubmissionID}`,
            uploadData,
            args
          )
          // eslint-disable-next-line no-unused-vars
          .then(function(response) {
            scope.isLoading = false;
            scope.uploadToTotalSubmissionFile = null;
            if (response.data.toLowerCase() === "background") {
              scope.$buefy.dialog.alert({
                message: `File is being processed for upload in the background. You will be notified via email once it is complete.`,
                confirmText: "OK",
                type: "is-success",
                onConfirm: () => window.close()
              });
            } else if (response.data.toLowerCase() === "failure") {
              scope.$buefy.dialog.alert({
                message: `A problem occurred with the upload file used. Please remove any special characters or extra line breaks on the description fields and try again. If the problem persists, please contact the IT Support at NewItem.IT.Support@thermofisher.com`,
                confirmText: "OK",
                type: "is-danger"
              });
            } else {
              scope.$buefy.dialog.alert({
                message: `File upload successful.`,
                confirmText: "OK",
                type: "is-success",
                onConfirm: () => scope.loadActiveTabData()
              });
            }
          })
          .catch(function(error) {
            scope.isLoading = false;
            scope.$buefy.dialog.alert({
              message: `A problem occurred with the attachment used. Please ensure attachment has the one of the following extensions: pdf, png, jpg, eps, tiff and try again. If the problem persists, please contact the IT Support at NewItem.IT.Support@thermofisher.com`,
              confirmText: "OK",
              type: "is-danger",
              onConfirm: () => (scope.uploadToTotalSubmissionFile = null)
            });
          });
      } else {
        scope.$buefy.dialog.alert({
          message: `"${scope.uploadToTotalSubmissionFile.name}" - Invalid file type, system accepts only pdf, xls, xlsx format.`,
          confirmText: "OK",
          type: "is-danger",
          onConfirm: () => (scope.uploadToTotalSubmissionFile = null)
        });
      }
    },
    uploadSubmissionAttachment() {
      var scope = this;
      var isUploadValid = /^.*\.(pdf|xls|xlsx)$/.test(
        this.submissionAttachment.name.toLowerCase()
      );
      if (isUploadValid) {
        var args = {
          headers: {
            "content-type": "form-data"
          }
        };
        var uploadData = new FormData();
        uploadData.append("attachment", scope.submissionAttachment);

        scope.isLoading = true;
        scope.$http
          .post(
            `${process.env.VUE_APP_API_BASE_DOMAIN}eu/uploadSubmissionHeaderAttachment?nbrNewItemCrKey=${scope.euSubmissionID}`,
            uploadData,
            args
          )
          // eslint-disable-next-line no-unused-vars
          .then(function(response) {
            scope.isLoading = false;
            scope.submissionAttachment = null;
            scope.$buefy.dialog.alert({
              message: `File upload successful.`,
              confirmText: "OK",
              type: "is-success",
              onConfirm: () => scope.loadSubmissionDetails()
            });
          })
          .catch(function(error) {
            scope.isLoading = false;
            scope.$buefy.dialog.alert({
              message: `A problem occurred with the attachment used. Please ensure attachment has the one of the following extensions: pdf, png, jpg, eps, tiff and try again. If the problem persists, please contact the IT Support at NewItem.IT.Support@thermofisher.com`,
              confirmText: "OK",
              type: "is-danger",
              onConfirm: () => (scope.submissionAttachment = null)
            });
          });
      } else {
        scope.$buefy.dialog.alert({
          message: `"${scope.submissionAttachment.name}" - Invalid file type, system accepts only pdf, xls, xlsx formats`,
          confirmText: "OK",
          type: "is-danger",
          onConfirm: () => (scope.submissionAttachment = null)
        });
      }
    },
    validateSubmissionLevel() {
      var supplierRequiredFieldsObjMap = {},
        scope = this;

      supplierRequiredFieldsObjMap = {
        key1: scope.suppNbr,
        key2: scope.suppName,
        key3: scope.website,
        key4: scope.submissionDesc,
        key5: scope.prodContactName,
        key6: scope.prodContactEmail,
        key7: scope.launchDate,
        key8: scope.regContactName,
        key9: scope.regContactPhone,
        key10: scope.regContactEmail
      };

      for (var index in supplierRequiredFieldsObjMap) {
        if (
          supplierRequiredFieldsObjMap[index] &&
          supplierRequiredFieldsObjMap[index] !== "" &&
          supplierRequiredFieldsObjMap[index] !== " " &&
          supplierRequiredFieldsObjMap[index] !== undefined &&
          supplierRequiredFieldsObjMap[index] !== null
        ) {
          //check if all entries have value
          this.isSupplierReqFieldsValid = true;
        } else {
          this.isSupplierReqFieldsValid = false;
          break;
        }
      }
    },
    validateSupplierSubmit(errorObj) {
      this.validateSubmissionLevel();
      if (window.uploadComplete) {
        if (
          this.isSupplierReqFieldsValid &&
          errorObj.totalErrorCount === 0 &&
          (this.giTotalResults > 0 ||
            this.piTotalResults > 0 ||
            this.riPtTotalResults > 0 ||
            this.riCbTotalResults > 0 ||
            this.riEiTotalResults > 0)
        ) {
          this.disableSupplierSubmit = false;
        } else {
          this.disableSupplierSubmit = true;
        }
      } else if (
        this.isSupplierReqFieldsValid &&
        errorObj.totalErrorCount === 0 &&
        errorObj.tabErrorCount === 0 &&
        this.disableSuppValidateBtn &&
        (this.giTotalResults > 0 ||
          this.piTotalResults > 0 ||
          this.riPtTotalResults > 0 ||
          this.riCbTotalResults > 0 ||
          this.riEiTotalResults > 0)
      ) {
        this.disableSupplierSubmit = false;
      } else {
        if (
          this.isSupplierReqFieldsValid &&
          this.disableSuppValidateBtn &&
          this.subGiTabErrorCount !== undefined &&
          this.subGiTabErrorCount === 0 &&
          this.subPiTabErrorCount !== undefined &&
          this.subPiTabErrorCount === 0 &&
          this.subRiProdTabErrorCount !== undefined &&
          this.subRiProdTabErrorCount === 0 &&
          this.subRiChemTabErrorCount !== undefined &&
          this.subRiChemTabErrorCount === 0 &&
          this.subRiChemTabErrorCount !== undefined &&
          this.subRiChemTabErrorCount === 0 &&
          (this.giTotalResults > 0 ||
            this.piTotalResults > 0 ||
            this.riPtTotalResults > 0 ||
            this.riCbTotalResults > 0 ||
            this.riEiTotalResults > 0)
        ) {
          this.disableSupplierSubmit = false;
        } else {
          this.disableSupplierSubmit = true;
        }
      }
      if (this.showItemDetail) {
        switch (this.activeTab) {
          case 0:
            this.updateGIErrorCount(errorObj.tabErrorCount);
            break;
          case 1:
            this.updatePIErrorCount(errorObj.tabErrorCount);
            break;
          case 2:
            this.updateRIPTErrorCount(errorObj.tabErrorCount);
            break;
          case 3:
            this.updateRICBErrorCount(errorObj.tabErrorCount);
            break;
          case 4:
            this.updateRIEIErrorCount(errorObj.tabErrorCount);
            break;
        }
      }
      if (window.uploadComplete) {
        window.uploadComplete = false;
      }
      //validate tab header css
      this.validateTabHeaders();
    },
    validateTabHeaders() {
      var tabHeader = document.querySelector(".tabs-wrapper.eu-tabs-wrapper");
      //validate errors on GI tab
      if (this.subGiTabErrorCount > 0) {
        if (this.giErrorCountOnTabOpen === 0) {
          tabHeader.classList.remove("gi-error");
        } else {
          tabHeader.classList.add("gi-error");
        }
      }
      //validate errors on PI tab
      if (this.subPiTabErrorCount > 0) {
        if (this.piErrorCountOnTabOpen === 0) {
          tabHeader.classList.remove("pi-error");
        } else {
          tabHeader.classList.add("pi-error");
        }
      }
      //validate errors on RIPT tab
      if (this.subRiProdTabErrorCount > 0) {
        if (this.riptErrorCountOnTabOpen === 0) {
          tabHeader.classList.remove("ript-error");
        } else {
          tabHeader.classList.add("ript-error");
        }
      }
      //validate errors on RICB tab
      if (this.subRiChemTabErrorCount > 0) {
        if (this.ricbErrorCountOnTabOpen === 0) {
          tabHeader.classList.remove("ricb-error");
        } else {
          tabHeader.classList.add("ricb-error");
        }
      }
      //validate errors on RIEI tab
      if (this.subRiEquipTabErrorCount > 0) {
        if (this.rieiErrorCountOnTabOpen === 0) {
          tabHeader.classList.remove("riei-error");
        } else {
          tabHeader.classList.add("riei-error");
        }
      }
    }
  }
};
</script>
